import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Decimal from 'decimal.js';
import { Link as RouterLink } from 'react-router-dom';
import {
  Alert,
  AlertIcon,
  Box,
  Button,
  Heading,
  Image,
  Input,
  Link,
  Select,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  VStack,
  HStack,
  Divider,
  Spinner,
  Textarea,
} from '@chakra-ui/react';

import {
  fiBasisFilingAuditNote,
  parseMultiExchangeCsvImport,
  splitCsvImportParts,
  EXCHANGE_CSV_PART_DELIM,
  buildCronosLedgerEventsUnpriced,
  applyExplorerHistoricPrices,
  filterFifoByTaxYear,
  buildOwnedWalletList,
  buildCexDepositWalletList,
  applyCexDepositCounterpartyClassification,
  buildCombinedCronosExplorerRawCsv,
  buildFinnishCapitalGainsCsv,
  parseCompiledExplorerDataset,
  normalizedLedgerToLedgerEvents,
  buildFiDiagnosticsCsv,
  buildFiIncomeAllTimeCsv,
  buildFiIncomePipelineDiagnosticsCsv,
  isTaxableIncomeLedgerEvent,
  TAX_PIPELINE,
  computeCdcCronosCashflowTotals,
  buildFiFiatReconciliationAllTimeCsv,
  buildFiFiatSummaryCsv,
  computeFiAuditReconciliationTotals,
  runLotBasisRepairPipeline,
  buildLotBasisRepairDiagnosticsCsv,
  clearFiTaxLocalStorage,
  mergeCryptoComFifoSlicesForDisplay,
  taxYearUtc,
  buildArbitrumLedgerEventsUnpriced,
  buildFullLedgerCsv,
  dedupeCryptoComVibanDisposals,
  parseDexScreenerCsv,
  dexScreenerRowsToPriceMap,
  applyDexScreenerLibraryToLedgerEvents,
} from '../cryptoTax';
import { getSisuForgeHubHref, isSisuForgeHubExternal } from '../sisuForgeHub';
import shieldUrl from '../assets/sisuforge/sisu-forge-shield.png';

const PUBLIC = process.env.PUBLIC_URL ?? '';

function newDexScreenerEntryId() {
  return typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
    ? crypto.randomUUID()
    : `dex-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

/** Cronos mainnet — same chain as `explorer-api.cronos.org/mainnet` in the import pipeline. */
const CRONOS_EXPLORER_BASE = 'https://cronoscan.com';

function cronosExplorerTxUrl(hash) {
  const h = String(hash || '')
    .trim()
    .toLowerCase();
  if (!/^0x[a-f0-9]{64}$/.test(h)) return null;
  return `${CRONOS_EXPLORER_BASE}/tx/${h}`;
}

function cronosExplorerAddressUrl(addr) {
  const a = String(addr || '')
    .trim()
    .toLowerCase();
  if (!/^0x[a-f0-9]{40}$/.test(a)) return null;
  return `${CRONOS_EXPLORER_BASE}/address/${a}`;
}

/** Inline 0x… tx hashes (64 hex) and addresses (40 hex) → Cronoscan links. */
function renderTextWithCronosLinks(text) {
  if (text == null || String(text).trim() === '') return '—';
  const s = String(text);
  const parts = s.split(/(0x[a-fA-F0-9]{40}|0x[a-fA-F0-9]{64})/g);
  return parts.map((part, i) => {
    if (/^0x[a-fA-F0-9]{64}$/.test(part)) {
      const href = cronosExplorerTxUrl(part);
      if (href) {
        return (
          <Link
            key={i}
            href={href}
            isExternal
            color="cyan.300"
            textDecoration="underline"
            fontWeight="medium"
          >
            {part}
          </Link>
        );
      }
    }
    if (/^0x[a-fA-F0-9]{40}$/.test(part)) {
      const href = cronosExplorerAddressUrl(part);
      if (href) {
        return (
          <Link
            key={i}
            href={href}
            isExternal
            color="cyan.300"
            textDecoration="underline"
            fontWeight="medium"
          >
            {part}
          </Link>
        );
      }
    }
    return <React.Fragment key={i}>{part}</React.Fragment>;
  });
}

function ExplorerTxOrAddressLink({ value, ...rest }) {
  const raw = value == null ? '' : String(value).trim();
  if (!raw) return '—';
  const txUrl = cronosExplorerTxUrl(raw);
  if (txUrl) {
    return (
      <Link href={txUrl} isExternal color="cyan.300" textDecoration="underline" wordBreak="break-all" {...rest}>
        {raw}
      </Link>
    );
  }
  const addrUrl = cronosExplorerAddressUrl(raw);
  if (addrUrl) {
    return (
      <Link href={addrUrl} isExternal color="cyan.300" textDecoration="underline" wordBreak="break-all" {...rest}>
        {raw}
      </Link>
    );
  }
  return (
    <Text as="span" wordBreak="break-word" {...rest}>
      {raw}
    </Text>
  );
}

function SummaryRefCell({ value }) {
  const raw = value == null ? '' : String(value).trim();
  if (!raw) {
    return (
      <Text fontSize="xs" opacity={0.5}>
        —
      </Text>
    );
  }
  if (/^(0x[a-fA-F0-9]{64}|0x[a-fA-F0-9]{40})$/i.test(raw)) {
    return (
      <Text fontSize="xs" fontFamily="mono" maxW="220px" sx={{ whiteSpace: 'normal', wordBreak: 'break-all' }}>
        <ExplorerTxOrAddressLink value={raw} />
      </Text>
    );
  }
  if (/0x[a-fA-F0-9]{40}/i.test(raw) || /0x[a-fA-F0-9]{64}/i.test(raw)) {
    return (
      <Text fontSize="xs" maxW="220px" sx={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
        {renderTextWithCronosLinks(raw)}
      </Text>
    );
  }
  return (
    <Text fontSize="xs" fontFamily="mono" maxW="220px" sx={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
      {raw}
    </Text>
  );
}

function csvCell(v) {
  if (v == null || String(v).trim() === '') return '—';
  return String(v);
}

/** Human-readable labels for Summary table “needs review” breakdown (FIFO disposal rows). */
const DISPOSAL_ATTENTION_REASON_LABELS = {
  valuation_fallback: 'Valuation: fallback outgoing',
  valuation_missing_proceeds: 'Valuation: missing proceeds review',
  deemed_prevented: 'Deemed basis blocked (zero proceeds)',
  provenance: 'Acquisition provenance review',
  proceeds_derivation: 'Proceeds derivation (fallback / none)',
  zero_cost_basis: 'Zero EUR cost basis',
};

/**
 * Flags disposal lines that may need manual review (non-exhaustive; aligns with CSV audit columns).
 */
function getDisposalAttentionMeta(d) {
  const reasons = [];
  const vf = d.valuationFlag;
  if (vf === 'fallback_outgoing_used') reasons.push('valuation_fallback');
  if (vf === 'missing_proceeds_review') reasons.push('valuation_missing_proceeds');
  if (d.deemedPreventedDueToZeroProceeds === true) reasons.push('deemed_prevented');
  if (d.acquisitionProvenanceFlag === 'missing_provenance_review') reasons.push('provenance');
  if (d.proceedsDerivation === 'outgoing_asset_fallback' || d.proceedsDerivation === 'none') {
    reasons.push('proceeds_derivation');
  }
  if (d.costEur && d.costEur.eq(0) && d.disposed && d.disposed.gt(0)) {
    reasons.push('zero_cost_basis');
  }
  return { needsAttention: reasons.length > 0, reasons };
}

function summarizeDisposalAttention(rows) {
  const problemIndices = [];
  const byReason = {};
  rows.forEach((d, i) => {
    const m = getDisposalAttentionMeta(d);
    if (!m.needsAttention) return;
    problemIndices.push(i);
    m.reasons.forEach((r) => {
      byReason[r] = (byReason[r] ?? 0) + 1;
    });
  });
  return { problemIndices, byReason };
}

function formatDisposalAttentionBreakdown(byReason) {
  const parts = Object.entries(byReason)
    .filter(([, n]) => n > 0)
    .map(([k, n]) => `${DISPOSAL_ATTENTION_REASON_LABELS[k] ?? k}: ${n}`);
  return parts.length ? parts.join(' · ') : '';
}

function compareDisposalRows(a, b, key, dir) {
  const sign = dir === 'asc' ? 1 : -1;
  const lc = (x, y) => String(x ?? '').localeCompare(String(y ?? ''), undefined, { numeric: true });
  switch (key) {
    case 'csvTimestamp':
      return lc(a.csvMeta?.timestampUtc, b.csvMeta?.timestampUtc) * sign;
    case 'csvDesc':
      return lc(a.csvMeta?.transactionDescription, b.csvMeta?.transactionDescription) * sign;
    case 'csvCurrency':
      return lc(a.csvMeta?.currency, b.csvMeta?.currency) * sign;
    case 'csvAmount':
      return lc(a.csvMeta?.amount, b.csvMeta?.amount) * sign;
    case 'csvToCurrency':
      return lc(a.csvMeta?.toCurrency, b.csvMeta?.toCurrency) * sign;
    case 'csvToAmount':
      return lc(a.csvMeta?.toAmount, b.csvMeta?.toAmount) * sign;
    case 'csvNativeCurrency':
      return lc(a.csvMeta?.nativeCurrency, b.csvMeta?.nativeCurrency) * sign;
    case 'csvNativeAmount':
      return lc(a.csvMeta?.nativeAmount, b.csvMeta?.nativeAmount) * sign;
    case 'csvNativeUsd':
      return lc(a.csvMeta?.nativeAmountUsd, b.csvMeta?.nativeAmountUsd) * sign;
    case 'csvKind':
      return lc(a.csvMeta?.transactionKind, b.csvMeta?.transactionKind) * sign;
    case 'csvHash':
      return lc(a.csvMeta?.transactionHash, b.csvMeta?.transactionHash) * sign;
    case 'ts':
      return (a.ts - b.ts) * sign;
    case 'lotAcquiredAt': {
      const la = a.lotAcquiredAt ?? -Infinity;
      const lb = b.lotAcquiredAt ?? -Infinity;
      return (la - lb) * sign;
    }
    case 'asset':
      return a.asset.localeCompare(b.asset) * sign;
    case 'disposed':
      return a.disposed.cmp(b.disposed) * sign;
    case 'proceedsEur':
      return a.proceedsEur.cmp(b.proceedsEur) * sign;
    case 'costEur':
      return a.costEur.cmp(b.costEur) * sign;
    case 'chosenCostBasisEur': {
      const ca = a.chosenCostBasisEur ?? a.fifoCostBasisEur ?? a.costEur;
      const cb = b.chosenCostBasisEur ?? b.fifoCostBasisEur ?? b.costEur;
      return ca.cmp(cb) * sign;
    }
    case 'gainEur':
      return a.gainEur.cmp(b.gainEur) * sign;
    case 'chosenGainEur': {
      const ga = a.chosenGainEur ?? a.gainEur;
      const gb = b.chosenGainEur ?? b.gainEur;
      return ga.cmp(gb) * sign;
    }
    case 'basisMethodUsed':
      return lc(a.basisMethodUsed, b.basisMethodUsed) * sign;
    case 'source':
      return String(a.source).localeCompare(String(b.source)) * sign;
    case 'ref':
      return String(a.ref ?? '').localeCompare(String(b.ref ?? '')) * sign;
    default:
      return 0;
  }
}

function escapeCsvField(val) {
  if (val == null || val === '') return '';
  const s = String(val);
  if (/[",\r\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}

function disposalDetailToCsvFields(d, index) {
  const m = d.csvMeta;
  const iso = new Date(d.ts).toISOString();
  const lotIso = d.lotAcquiredAt != null ? new Date(d.lotAcquiredAt).toISOString() : '';
  const originTsIso =
    d.originTimestamp != null ? new Date(d.originTimestamp).toISOString() : '';
  const fifoCost = d.fifoCostBasisEur ?? d.costEur;
  const deemedCost = d.deemedCostBasisEur ?? d.proceedsEur.mul(0.2);
  const chosenCost = d.chosenCostBasisEur ?? fifoCost;
  const fifoGain = d.fifoGainEur ?? d.gainEur;
  const deemedGain = d.deemedGainEur ?? d.proceedsEur.minus(deemedCost);
  const chosenGain = d.chosenGainEur ?? d.gainEur;
  const meta = TAX_PIPELINE.fi;
  const basisUsed = d.basisMethodUsed ?? meta.basisMethod;
  const bracket = d.deemedHoldingBracket ?? '';
  const cryptoCols = m
    ? [
        m.timestampUtc,
        m.transactionDescription,
        m.currency,
        m.amount,
        m.toCurrency,
        m.toAmount,
        m.nativeCurrency,
        m.nativeAmount,
        m.nativeAmountUsd,
        m.transactionKind,
        m.transactionHash,
      ]
    : ['', '', '', '', '', '', '', '', '', '', ''];
  const deemedPrev =
    d.deemedPreventedDueToZeroProceeds === true ? 'yes' : '';
  const lr = d.lotBasisRepair;
  return [
    meta.policyMode,
    meta.basisMethod,
    meta.timezone,
    fiBasisFilingAuditNote(),
    fifoCost.toFixed(6),
    deemedCost.toFixed(6),
    chosenCost.toFixed(6),
    basisUsed,
    fifoGain.toFixed(6),
    deemedGain.toFixed(6),
    chosenGain.toFixed(6),
    bracket,
    d.proceedsDerivation ?? '',
    d.valuationFlag ?? '',
    deemedPrev,
    lr ? 'yes' : '',
    lr?.basisRepairMethod ?? '',
    lr?.basisRepairConfidence ?? '',
    lr?.basisRepairSourceRef ?? '',
    ...cryptoCols,
    String(index),
    iso.slice(0, 10),
    iso.slice(11, 19),
    iso,
    d.asset,
    d.disposed.toFixed(8),
    d.proceedsEur.toFixed(2),
    d.costEur.toFixed(2),
    d.gainEur.toFixed(2),
    d.gainEur.gte(0) ? 'gain' : 'loss',
    d.source,
    d.ref ?? '',
    d.lotAcquiredAt != null ? lotIso.slice(0, 10) : '',
    d.lotAcquiredAt != null ? lotIso.slice(11, 19) : '',
    lotIso,
    d.lotId ?? '',
    d.originLotId ?? '',
    originTsIso,
    d.originSourceRef ?? '',
    d.originWallet ?? '',
    d.currentWallet ?? '',
    d.basisContinuityRef ?? '',
    d.walletContinuityKind ?? '',
    d.acquisitionProvenance ?? '',
    d.acquisitionProvenanceFlag ?? '',
    d.fifoMergedFromSlices != null ? String(d.fifoMergedFromSlices) : '',
  ];
}

const CSV_HEADERS = [
  'policy_mode',
  'basis_method',
  'timezone',
  'filing_basis_audit_note',
  'fifo_cost_basis_eur',
  'deemed_cost_basis_eur',
  'chosen_cost_basis_eur',
  'basis_method_used',
  'gain_loss_eur_fifo',
  'gain_loss_eur_deemed',
  'gain_loss_eur_chosen_filing',
  'deemed_holding_bracket',
  'proceeds_derivation',
  'valuation_flag',
  'deemed_prevented_zero_proceeds',
  'lot_basis_repaired',
  'lot_basis_repair_method',
  'lot_basis_repair_confidence',
  'lot_basis_repair_source_ref',
  'Timestamp (UTC) [Crypto.com CSV]',
  'Transaction Description',
  'Currency',
  'Amount',
  'To Currency',
  'To Amount',
  'Native Currency',
  'Native Amount',
  'Native Amount (in USD)',
  'Transaction Kind',
  'Transaction Hash',
  'Index',
  'FIFO disposal date (UTC)',
  'FIFO disposal time (UTC)',
  'FIFO disposal ISO 8601 (UTC)',
  'Asset (FIFO)',
  'Quantity disposed (FIFO)',
  'Proceeds EUR',
  'Cost basis EUR',
  'Gain or loss EUR',
  'Result',
  'Source',
  'Reference',
  'Lot acquired date (UTC)',
  'Lot acquired time (UTC)',
  'Lot acquired ISO 8601 (UTC)',
  'lot_id',
  'origin_lot_id',
  'origin_timestamp_iso_utc',
  'origin_source_ref',
  'origin_wallet',
  'current_wallet',
  'basis_continuity_ref',
  'wallet_continuity_kind',
  'acquisition_provenance',
  'acquisition_provenance_flag',
  'fifo_merged_lot_slices',
];

function buildDisposalsCsv(rows) {
  const lines = [
    CSV_HEADERS.map(escapeCsvField).join(','),
    ...rows.map((d, i) => disposalDetailToCsvFields(d, i + 1).map(escapeCsvField).join(',')),
  ];
  return lines.join('\r\n');
}

function downloadUtf8Csv(filename, text) {
  const blob = new Blob([`\uFEFF${text}`], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.rel = 'noopener';
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

const VERO_URL =
  'https://vero.fi/en/individuals/property/investments/virtual-currencies';

/** Table pagination for full ledger (can be thousands of Cronos rows). */
const LEDGER_PAGE_SIZE = 100;

function yearsOptions() {
  const y = new Date().getFullYear();
  const list = [];
  for (let i = y; i >= 2017; i--) list.push(i);
  return list;
}

export default function CryptoTaxFinland() {
  /** Each mount / full page load: drop `sisuforge.fi.*` storage so imports drive state, not last session. */
  const fiTaxStorageClearedRef = useRef(false);
  if (typeof window !== 'undefined' && !fiTaxStorageClearedRef.current) {
    fiTaxStorageClearedRef.current = true;
    clearFiTaxLocalStorage();
  }

  /** Calendar year filter, or all disposals across imported history. */
  const [taxPeriod, setTaxPeriod] = useState(() => new Date().getFullYear());
  const [csvText, setCsvText] = useState('');
  const [csvSkipped, setCsvSkipped] = useState([]);
  /** Latest CSV parse diagnostics (non-taxable internal / ambiguous); accumulates across Parse imports. */
  const [csvFiDiagnostics, setCsvFiDiagnostics] = useState([]);
  const [cronosAddr, setCronosAddr] = useState('');
  /** Cronos Explorer API key — also supported via REACT_APP_CRONOS_EXPLORER_API_KEY in `.env`. */
  const [cronosExplorerApiKey, setCronosExplorerApiKey] = useState(
    () => (process.env.REACT_APP_CRONOS_EXPLORER_API_KEY || '').trim()
  );
  /** Extra `Label|0x…` or `0x…` lines — all treated as owned for fetch + internal-transfer detection. */
  const [cronosExtraWallets, setCronosExtraWallets] = useState('');
  /** CEX deposit / hot wallet addresses (shared) — never fetched; used only to mark deposit/withdrawal legs non-taxable. */
  const [cronosCexDepositWallets, setCronosCexDepositWallets] = useState('');
  /** Counterparty contracts that pay **to** your wallets — incoming tokentx/native from these → taxable income (merged with code defaults). */
  const [cronosIncomePayerAddresses, setCronosIncomePayerAddresses] = useState('');
  /** Optional `YYYY-MM-DD` (UTC day bounds); filters rows after fetch. */
  const [cronosDateFrom, setCronosDateFrom] = useState('');
  const [cronosDateTo, setCronosDateTo] = useState('');
  const [cronosWarnings, setCronosWarnings] = useState([]);
  const [ledgerEvents, setLedgerEvents] = useState([]);
  /** CDC cash / fiat-only rows (not in FIFO). */
  const [fiatCashEvents, setFiatCashEvents] = useState([]);
  const [loadingCsv, setLoadingCsv] = useState(false);
  const [readingCsvFiles, setReadingCsvFiles] = useState(false);
  const [loadingCronos, setLoadingCronos] = useState(false);
  const [loadingCronosPrices, setLoadingCronosPrices] = useState(false);
  const [cronosMsg, setCronosMsg] = useState('');
  const [cronosMissingPrices, setCronosMissingPrices] = useState([]);
  /** Each DexScreener CSV with its ledger token symbol (multi-token library). */
  const [dexScreenerLibrary, setDexScreenerLibrary] = useState([]);
  const [loadingDexScreenerApply, setLoadingDexScreenerApply] = useState(false);
  /** Heavy tables / long logs hidden by default — reduces DOM work and scroll jank. */
  const [showFullLedgerTable, setShowFullLedgerTable] = useState(false);
  const [showDisposalDetailTable, setShowDisposalDetailTable] = useState(false);
  const [showActivityLog, setShowActivityLog] = useState(false);
  const [showCsvSkippedPanel, setShowCsvSkippedPanel] = useState(false);
  const [showExplorerMissingPanel, setShowExplorerMissingPanel] = useState(false);
  /** Last Cronos fetch: full `tokentx` / `txlist` payloads (deduped) for CSV archival. */
  const [cronosRawExplorer, setCronosRawExplorer] = useState(null);
  /** Etherscan API V2 key (multichain; Arbitrum = chainid 42161) — REACT_APP_ARBISCAN_API_KEY or REACT_APP_ETHERSCAN_API_KEY in `.env`. */
  const [arbitrumExplorerApiKey, setArbitrumExplorerApiKey] = useState(
    () =>
      (process.env.REACT_APP_ARBISCAN_API_KEY || process.env.REACT_APP_ETHERSCAN_API_KEY || '').trim()
  );
  const [loadingArbitrum, setLoadingArbitrum] = useState(false);
  const [arbitrumMsg, setArbitrumMsg] = useState('');
  const [arbitrumWarnings, setArbitrumWarnings] = useState([]);
  const [arbitrumRawExplorer, setArbitrumRawExplorer] = useState(null);
  const [compiledExplorerText, setCompiledExplorerText] = useState('');
  const [compiledExplorerFilesLabel, setCompiledExplorerFilesLabel] = useState('');
  const [loadingCompiledExplorer, setLoadingCompiledExplorer] = useState(false);
  const [csvFilesLabel, setCsvFilesLabel] = useState('');
  const [activityLog, setActivityLog] = useState([]);
  const csvFileInputRef = useRef(null);
  const compiledExplorerFileInputRef = useRef(null);
  const dexScreenerFileInputRef = useRef(null);
  /** Filenames for last multi-select, aligned with `splitCsvImportParts(csvText)` order. */
  const csvPartHintsRef = useRef([]);
  /** AbortController for CSV parse (merge into ledger); allows partial results. */
  const csvParseAbortRef = useRef(null);

  const pushLog = useCallback((line) => {
    const ts = new Date().toLocaleTimeString(undefined, {
      hour12: false,
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
    setActivityLog((prev) => [...prev.slice(-150), `${ts}  ${line}`]);
  }, []);

  const csvBusy =
    readingCsvFiles || loadingCsv || loadingCompiledExplorer || loadingArbitrum;

  /** Cronos or Arbitrum explorer rows with price hints but no EUR yet. */
  const explorerAwaitingPrices = useMemo(
    () =>
      ledgerEvents.filter(
        (e) =>
          (e.source === 'cronos' || e.source === 'arbitrum') &&
          e.eurValue == null &&
          e.cronosPriceHint != null
      ).length,
    [ledgerEvents]
  );

  /** UTC year for DexScreener date cells that omit the year (matches reporting period when set). */
  const dexScreenerDefaultYear = useMemo(
    () => (taxPeriod === 'all' ? new Date().getUTCFullYear() : taxPeriod),
    [taxPeriod]
  );

  const dexScreenerParsedLibrary = useMemo(() => {
    return dexScreenerLibrary.map((e) => ({
      id: e.id,
      filename: e.filename,
      symbol: e.symbol,
      rawText: e.rawText,
      parsed: parseDexScreenerCsv(e.rawText, {
        defaultYear: dexScreenerDefaultYear,
        assetSymbol: e.symbol.trim() || undefined,
      }),
    }));
  }, [dexScreenerLibrary, dexScreenerDefaultYear]);

  const dexScreenerLibraryApplyReady = useMemo(() => {
    return dexScreenerParsedLibrary
      .filter((e) => e.symbol.trim() && e.parsed.rows.length > 0)
      .map((e) => ({
        assetSymbol: e.symbol.trim(),
        priceByTxHash: dexScreenerRowsToPriceMap(e.parsed.rows),
      }));
  }, [dexScreenerParsedLibrary]);

  const internalTransferCount = useMemo(
    () => ledgerEvents.filter((e) => e.kind === 'internal_transfer').length,
    [ledgerEvents]
  );

  const sortedLedgerEvents = useMemo(() => {
    return [...ledgerEvents].sort((a, b) => a.ts - b.ts);
  }, [ledgerEvents]);

  const cashflowTotals = useMemo(
    () => computeCdcCronosCashflowTotals(ledgerEvents, taxPeriod),
    [ledgerEvents, taxPeriod]
  );

  const auditReconciliationTotals = useMemo(
    () => computeFiAuditReconciliationTotals(ledgerEvents, fiatCashEvents),
    [ledgerEvents, fiatCashEvents]
  );

  /** Used for wallet-scoped FIFO + lot continuity (same list as Cronos fetch / compiled explorer). */
  const ownedWalletsForFifo = useMemo(() => {
    const { wallets, error } = buildOwnedWalletList(cronosAddr, cronosExtraWallets);
    if (error) return [];
    return wallets;
  }, [cronosAddr, cronosExtraWallets]);

  const cexDepositWalletsList = useMemo(
    () => buildCexDepositWalletList(cronosCexDepositWallets),
    [cronosCexDepositWallets]
  );

  const ownedAddressSetForCex = useMemo(() => {
    const s = new Set();
    for (const w of ownedWalletsForFifo) {
      s.add(w.address);
    }
    return s;
  }, [ownedWalletsForFifo]);

  const cexDepositAddressSet = useMemo(
    () => new Set(cexDepositWalletsList.map((w) => w.address.toLowerCase())),
    [cexDepositWalletsList]
  );

  const [ledgerPage, setLedgerPage] = useState(1);
  useEffect(() => {
    setLedgerPage(1);
  }, [ledgerEvents.length]);

  const ledgerPageCount = Math.max(1, Math.ceil(sortedLedgerEvents.length / LEDGER_PAGE_SIZE));
  const ledgerPageSafe = Math.min(ledgerPage, ledgerPageCount);
  const ledgerPageSlice = sortedLedgerEvents.slice(
    (ledgerPageSafe - 1) * LEDGER_PAGE_SIZE,
    ledgerPageSafe * LEDGER_PAGE_SIZE
  );

  const exportFullLedgerCsv = useCallback(() => {
    if (!sortedLedgerEvents.length) return;
    downloadUtf8Csv('crypto-tax-fi-full-ledger.csv', buildFullLedgerCsv(sortedLedgerEvents));
  }, [sortedLedgerEvents]);

  const exportFiDiagnosticsCsv = useCallback(() => {
    if (!csvFiDiagnostics.length) return;
    downloadUtf8Csv(
      'crypto-tax-fi-diagnostics.csv',
      buildFiDiagnosticsCsv(csvFiDiagnostics)
    );
  }, [csvFiDiagnostics]);

  const incomeLedgerRows = useMemo(
    () => sortedLedgerEvents.filter(isTaxableIncomeLedgerEvent),
    [sortedLedgerEvents]
  );

  /** Sum of taxable income FMV rows — separate from capital gains (FIFO disposals). */
  const taxableIncomeForReportingPeriod = useMemo(() => {
    const rows = incomeLedgerRows.filter((e) => {
      if (e.eurValue == null || !e.eurValue.gt(0)) return false;
      if (taxPeriod === 'all') return true;
      return taxYearUtc(e.ts) === taxPeriod;
    });
    let total = new Decimal(0);
    for (const e of rows) {
      if (e.eurValue != null) total = total.plus(e.eurValue);
    }
    return { totalEur: total, rowCount: rows.length };
  }, [incomeLedgerRows, taxPeriod]);

  const exportFiIncomeAllTimeCsv = useCallback(() => {
    if (!incomeLedgerRows.length) return;
    downloadUtf8Csv(
      'crypto-tax-fi-income-all-time.csv',
      buildFiIncomeAllTimeCsv(ledgerEvents)
    );
  }, [incomeLedgerRows.length, ledgerEvents]);

  const exportFiIncomePipelineDiagnosticsCsv = useCallback(() => {
    const text = buildFiIncomePipelineDiagnosticsCsv(ledgerEvents);
    if (text.split(/\r?\n/).length <= 1) return;
    downloadUtf8Csv('crypto-tax-fi-income-pipeline-diagnostics.csv', text);
  }, [ledgerEvents]);

  const exportFiFiatReconciliationCsv = useCallback(() => {
    if (!fiatCashEvents.length) return;
    downloadUtf8Csv(
      'crypto-tax-fi-fiat-reconciliation-all-time.csv',
      buildFiFiatReconciliationAllTimeCsv(fiatCashEvents)
    );
  }, [fiatCashEvents]);

  const exportFiFiatSummaryCsv = useCallback(() => {
    if (!fiatCashEvents.length) return;
    downloadUtf8Csv(
      'crypto-tax-fi-fiat-summary.csv',
      buildFiFiatSummaryCsv(fiatCashEvents)
    );
  }, [fiatCashEvents]);

  const [lotRepairPipeline, setLotRepairPipeline] = useState(null);
  const [lotRepairLoading, setLotRepairLoading] = useState(false);
  const [capitalGainsExporting, setCapitalGainsExporting] = useState(false);

  useEffect(() => {
    if (!ledgerEvents.length) {
      setLotRepairPipeline(null);
      return;
    }
    let cancelled = false;
    setLotRepairLoading(true);
    runLotBasisRepairPipeline(ledgerEvents, {
      ownedWallets: ownedWalletsForFifo,
    })
      .then((r) => {
        if (!cancelled) setLotRepairPipeline(r);
      })
      .catch(() => {
        if (!cancelled) setLotRepairPipeline(null);
      })
      .finally(() => {
        if (!cancelled) setLotRepairLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [ledgerEvents, ownedWalletsForFifo]);

  const fifoAll = lotRepairPipeline?.pass2WithFi ?? null;

  const exportLotBasisRepairDiagnosticsCsv = useCallback(() => {
    if (!lotRepairPipeline?.stats) return;
    downloadUtf8Csv(
      'crypto-tax-fi-lot-basis-repair-diagnostics.csv',
      buildLotBasisRepairDiagnosticsCsv(lotRepairPipeline.stats)
    );
  }, [lotRepairPipeline]);

  const fifoDisplay = useMemo(() => {
    if (!fifoAll) return null;
    if (taxPeriod === 'all') return fifoAll;
    return filterFifoByTaxYear(fifoAll, taxPeriod);
  }, [fifoAll, taxPeriod]);

  const [disposalSort, setDisposalSort] = useState(() => ({
    key: 'ts',
    dir: 'asc',
  }));

  useEffect(() => {
    setDisposalSort({ key: 'ts', dir: 'asc' });
  }, [taxPeriod]);

  const sortedDisposalDetails = useMemo(() => {
    const details = fifoDisplay?.details;
    if (!details?.length) return [];
    const rows = [...details];
    rows.sort((a, b) => compareDisposalRows(a, b, disposalSort.key, disposalSort.dir));
    return rows;
  }, [fifoDisplay?.details, disposalSort]);

  /** One Crypto.com CSV disposal can split into several FIFO lot slices; merge for display/CSV while totals stay pipeline-accurate. */
  const disposalDetailsForView = useMemo(() => {
    const merged = mergeCryptoComFifoSlicesForDisplay(sortedDisposalDetails);
    const rows = [...merged];
    rows.sort((a, b) => compareDisposalRows(a, b, disposalSort.key, disposalSort.dir));
    return rows;
  }, [sortedDisposalDetails, disposalSort]);

  const disposalAttentionSummary = useMemo(
    () => summarizeDisposalAttention(disposalDetailsForView),
    [disposalDetailsForView]
  );
  const { problemIndices, byReason } = disposalAttentionSummary;

  const [problemNavFocus, setProblemNavFocus] = useState(0);

  useEffect(() => {
    setProblemNavFocus(0);
  }, [disposalDetailsForView, disposalSort]);

  useEffect(() => {
    setProblemNavFocus((f) => {
      const n = problemIndices.length;
      if (n === 0) return 0;
      return Math.min(f, n - 1);
    });
  }, [problemIndices.length]);

  const goProblemStep = useCallback(
    (delta) => {
      const n = problemIndices.length;
      if (!n) return;
      setProblemNavFocus((prev) => {
        const next = (prev + delta + n) % n;
        const rowIdx = problemIndices[next];
        requestAnimationFrame(() => {
          document.getElementById(`disposal-summary-row-${rowIdx}`)?.scrollIntoView({
            block: 'nearest',
            behavior: 'smooth',
          });
        });
        return next;
      });
    },
    [problemIndices]
  );

  const toggleDisposalSort = useCallback((key) => {
    setDisposalSort((prev) =>
      prev.key === key ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'asc' }
    );
  }, []);

  const exportDisposalsCsv = useCallback(() => {
    if (!disposalDetailsForView.length) return;
    const label = taxPeriod === 'all' ? 'all-time' : String(taxPeriod);
    downloadUtf8Csv(
      `crypto-tax-fi-disposals-${label}.csv`,
      buildDisposalsCsv(disposalDetailsForView)
    );
  }, [disposalDetailsForView, taxPeriod]);

  /** Finnish-style report + ECB USD/EUR on sale date + Excel helpers for manual USD proceeds. */
  const exportFinnishCapitalGainsCsv = useCallback(async () => {
    if (!disposalDetailsForView.length) return;
    setCapitalGainsExporting(true);
    try {
      const label = taxPeriod === 'all' ? 'all-time' : String(taxPeriod);
      const csv = await buildFinnishCapitalGainsCsv(disposalDetailsForView);
      downloadUtf8Csv(`crypto-tax-fi-capital-gains-${label}.csv`, csv);
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      window.alert(
        `Capital gains export failed (ECB USD→EUR via Frankfurter). Check network / api.frankfurter.dev.\n\n${msg}`
      );
    } finally {
      setCapitalGainsExporting(false);
    }
  }, [disposalDetailsForView, taxPeriod]);

  const exportCronosRawExplorerCsv = useCallback(() => {
    if (!cronosRawExplorer) return;
    const { tokenTransfers, nativeTransactions, internalTransactions } = cronosRawExplorer;
    if (!tokenTransfers.length && !nativeTransactions.length) return;
    const text = buildCombinedCronosExplorerRawCsv(
      tokenTransfers,
      nativeTransactions,
      internalTransactions ?? []
    );
    downloadUtf8Csv('cronos-explorer-raw-combined.csv', text);
  }, [cronosRawExplorer]);

  const exportArbitrumRawExplorerCsv = useCallback(() => {
    if (!arbitrumRawExplorer) return;
    const { tokenTransfers, nativeTransactions, internalTransactions } = arbitrumRawExplorer;
    if (!tokenTransfers.length && !nativeTransactions.length && !internalTransactions?.length) return;
    const text = buildCombinedCronosExplorerRawCsv(
      tokenTransfers,
      nativeTransactions,
      internalTransactions ?? []
    );
    downloadUtf8Csv('arbitrum-explorer-raw-combined.csv', text);
  }, [arbitrumRawExplorer]);

  const onFile = useCallback(
    async (e) => {
      const files = e.target.files;
      if (!files?.length) return;

      const fileArr = Array.from(files);
      const label =
        fileArr.length === 1
          ? fileArr[0].name
          : `${fileArr.length} files: ${fileArr.map((f) => f.name).join(', ')}`;

      const readOne = (file) =>
        new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () =>
            resolve(typeof reader.result === 'string' ? reader.result : '');
          reader.onerror = () => reject(reader.error);
          reader.readAsText(file);
        });

      setReadingCsvFiles(true);
      pushLog(`Selected ${fileArr.length} file(s). Reading from disk…`);

      try {
        const texts = [];
        for (let i = 0; i < fileArr.length; i++) {
          pushLog(`Reading ${i + 1}/${fileArr.length}: ${fileArr[i].name}`);
          texts.push(await readOne(fileArr[i]));
        }
        csvPartHintsRef.current = fileArr.map((f) => f.name);
        const merged =
          texts.length === 1
            ? texts[0]
            : texts.join(EXCHANGE_CSV_PART_DELIM);
        setCsvText(merged);
        setCsvFilesLabel(label);
        const lineCount = merged.split(/\r?\n/).filter((l) => l.trim()).length;
        pushLog(
          fileArr.length > 1
            ? `Loaded ${fileArr.length} CSV part(s) (${lineCount} lines total, ${(merged.length / 1024).toFixed(1)} KB). Each part keeps its own header — click Parse to detect Binance / Coinbase / Crypto.com per file.`
            : `Merge complete: ${lineCount} lines, ${(merged.length / 1024).toFixed(1)} KB. Click "Parse & merge into ledger".`
        );
      } catch (err) {
        pushLog(`Error reading files: ${String(err?.message || err)}`);
        setCsvFilesLabel('');
      } finally {
        setReadingCsvFiles(false);
      }
    },
    [pushLog]
  );

  const stopCsvParse = useCallback(() => {
    csvParseAbortRef.current?.abort();
    pushLog('Stop requested — will merge events parsed so far…');
  }, [pushLog]);

  const importCsv = useCallback(async () => {
    if (!csvText.trim()) {
      pushLog('No CSV text yet — choose files or paste into the box above.');
      return;
    }
    csvParseAbortRef.current?.abort();
    const ac = new AbortController();
    csvParseAbortRef.current = ac;
    setLoadingCsv(true);
    pushLog('Parse started (USD→EUR uses ECB rates; large files take time).');
    try {
      const partCount = splitCsvImportParts(csvText).length;
      const hintList = csvPartHintsRef.current;
      const partFilenameHints =
        Array.isArray(hintList) && hintList.length === partCount ? hintList : undefined;
      const { events, skipped, aborted, diagnostics, fiatCashEvents: fiatIn, blockSummaries } =
        await parseMultiExchangeCsvImport(csvText, {
          signal: ac.signal,
          onProgress: (msg) => pushLog(msg),
          partFilenameHints,
        });
      if (blockSummaries?.length > 1) {
        blockSummaries.forEach((b, i) => {
          pushLog(
            `CSV part ${i + 1}: ${b.detected_exchange} / ${b.detected_schema_family} (score ${b.detection_score}) → ${b.events_emitted} event(s)`
          );
        });
      }
      setCsvSkipped(skipped);
      setCsvFiDiagnostics((prev) => [...prev, ...diagnostics]);
      setLedgerEvents((prev) => mergeEvents(prev, events));
      setFiatCashEvents((prev) => mergeFiatCashEvents(prev, fiatIn));
      pushLog(
        aborted
          ? `Parse stopped — merged +${events.length} crypto events, +${fiatIn.length} fiat cash rows (partial; re-run import to continue from full CSV if needed).`
          : `Ledger updated: +${events.length} crypto events, +${fiatIn.length} fiat cash (reconciliation only).`
      );
    } catch (err) {
      pushLog(`Parse error: ${String(err?.message || err)}`);
    } finally {
      csvParseAbortRef.current = null;
      setLoadingCsv(false);
    }
  }, [csvText, pushLog]);

  const fetchCronosTransactions = useCallback(async () => {
    const explorerApiKey =
      cronosExplorerApiKey.trim() ||
      (process.env.REACT_APP_CRONOS_EXPLORER_API_KEY || '').trim();
    if (!explorerApiKey) {
      pushLog(
        'Cronos: add your Cronos Explorer API key in the field below, or set REACT_APP_CRONOS_EXPLORER_API_KEY in sisuforge/.env and restart the dev server (see .env.example).'
      );
      return;
    }
    const { wallets, error } = buildOwnedWalletList(cronosAddr, cronosExtraWallets);
    if (error || wallets.length === 0) {
      pushLog(`Cronos: ${error || 'No valid wallets.'}`);
      return;
    }
    const timeFromMs = cronosDateFrom
      ? new Date(`${cronosDateFrom}T00:00:00.000Z`).getTime()
      : undefined;
    const timeToMs = cronosDateTo
      ? new Date(`${cronosDateTo}T23:59:59.999Z`).getTime()
      : undefined;

    setLoadingCronos(true);
    setCronosMsg('');
    setCronosWarnings([]);
    setCronosMissingPrices([]);
    pushLog(
      `Cronos: fetching ${wallets.length} owned wallet(s) (no EUR yet)${timeFromMs != null || timeToMs != null ? ' — date filter after fetch' : ''}…`
    );
    try {
      const { events, warnings, meta, rawExplorer, tokenRegistry, diagnostics: cronosFiDiag } =
        await buildCronosLedgerEventsUnpriced({
          wallets,
          apiKey: explorerApiKey,
          timeFromMs,
          timeToMs,
          incomePayerAddressLines: cronosIncomePayerAddresses.split(/\r?\n/).map((s) => s.trim()).filter(Boolean),
          ...(cexDepositWalletsList.length > 0 ? { cexDepositWallets: cexDepositWalletsList } : {}),
          onProgress: (msg) => {
            setCronosMsg(msg);
            pushLog(`Cronos: ${msg}`);
          },
        });
      setCronosRawExplorer(rawExplorer);
      setCronosWarnings(warnings);
      if (cronosFiDiag?.length) {
        setCsvFiDiagnostics((prev) => [...prev, ...cronosFiDiag]);
      }
      setLedgerEvents((prev) => mergeEvents(prev, events));
      pushLog(
        `Cronos: merged ${events.length} ledger row(s) — wallet-first tokentx (no token list). ` +
          `Registry: ${meta.tokenRegistrySize} token contract(s); ` +
          `unique tokentx: ${meta.dedupedTokenTransfers}, txlist: ${meta.dedupedNativeTx}, internal: ${meta.dedupedInternalTx}` +
          `${meta.contractBackfillRowsAdded ? `, backfill +${meta.contractBackfillRowsAdded}` : ''}` +
          ` — internal (non-tax) transfers: ${meta.internalTransferCount}` +
          `${meta.routerSwapInternalCroLegsInjected ? `; router CRO leg(s) from internal tx: +${meta.routerSwapInternalCroLegsInjected}` : ''}` +
          `${meta.revertedOrFailedTxRowsSkipped ? `; skipped reverted/failed tx rows: ${meta.revertedOrFailedTxRowsSkipped}` : ''}` +
          `${meta.timeFilteredOut ? `, filtered by date: ${meta.timeFilteredOut}` : ''}. ` +
          `${meta.cexDepositReclassified ? `CEX deposit counterparty reclassified: ${meta.cexDepositReclassified}. ` : ''}` +
          `Fetch EUR when ready.`
      );
      if (tokenRegistry?.length) {
        pushLog(
          `Cronos: token registry sample — ${tokenRegistry
            .slice(0, 5)
            .map((t) => `${t.tokenSymbol} (${t.contractAddress.slice(0, 8)}…×${t.transferCount})`)
            .join('; ')}${tokenRegistry.length > 5 ? '…' : ''}`
        );
      }
    } catch (err) {
      const msg = String(err?.message || err);
      setCronosWarnings([msg]);
      pushLog(`Cronos error: ${msg}`);
    } finally {
      setLoadingCronos(false);
      setCronosMsg('');
    }
  }, [
    cronosAddr,
    cronosExplorerApiKey,
    cronosExtraWallets,
    cronosDateFrom,
    cronosDateTo,
    cronosIncomePayerAddresses,
    cexDepositWalletsList,
    pushLog,
  ]);

  const fetchArbitrumTransactions = useCallback(async () => {
    const key =
      arbitrumExplorerApiKey.trim() ||
      (process.env.REACT_APP_ARBISCAN_API_KEY || '').trim() ||
      (process.env.REACT_APP_ETHERSCAN_API_KEY || '').trim();
    if (!key) {
      pushLog(
        'Arbitrum: add your Etherscan API V2 key below (works for Arbitrum), or set REACT_APP_ARBISCAN_API_KEY / REACT_APP_ETHERSCAN_API_KEY in sisuforge/.env — create a key at https://etherscan.io/apidashboard (see .env.example).'
      );
      return;
    }
    const { wallets, error } = buildOwnedWalletList(cronosAddr, cronosExtraWallets);
    if (error || wallets.length === 0) {
      pushLog(`Arbitrum: ${error || 'No valid wallets.'}`);
      return;
    }
    const timeFromMs = cronosDateFrom
      ? new Date(`${cronosDateFrom}T00:00:00.000Z`).getTime()
      : undefined;
    const timeToMs = cronosDateTo
      ? new Date(`${cronosDateTo}T23:59:59.999Z`).getTime()
      : undefined;

    setLoadingArbitrum(true);
    setArbitrumMsg('');
    setArbitrumWarnings([]);
    pushLog(
      `Arbitrum: fetching ${wallets.length} wallet(s) (no EUR yet)${timeFromMs != null || timeToMs != null ? ' — date filter after fetch' : ''}…`
    );
    try {
      const { events, warnings, meta, rawExplorer, tokenRegistry, diagnostics: arbFiDiag } =
        await buildArbitrumLedgerEventsUnpriced({
          wallets,
          apiKey: key,
          timeFromMs,
          timeToMs,
          incomePayerAddressLines: cronosIncomePayerAddresses
            .split(/\r?\n/)
            .map((s) => s.trim())
            .filter(Boolean),
          ...(cexDepositWalletsList.length > 0 ? { cexDepositWallets: cexDepositWalletsList } : {}),
          onProgress: (msg) => {
            setArbitrumMsg(msg);
            pushLog(`Arbitrum: ${msg}`);
          },
        });
      setArbitrumRawExplorer(rawExplorer);
      setArbitrumWarnings(warnings);
      if (arbFiDiag?.length) {
        setCsvFiDiagnostics((prev) => [...prev, ...arbFiDiag]);
      }
      setLedgerEvents((prev) => mergeEvents(prev, events));
      pushLog(
        `Arbitrum: merged ${events.length} ledger row(s). ` +
          `Registry: ${meta.tokenRegistrySize} token contract(s); ` +
          `unique tokentx: ${meta.dedupedTokenTransfers}, txlist: ${meta.dedupedNativeTx}, internal: ${meta.dedupedInternalTx}` +
          ` — internal (non-tax) transfers: ${meta.internalTransferCount}` +
          `${meta.revertedOrFailedTxRowsSkipped ? `; skipped reverted/failed tx rows: ${meta.revertedOrFailedTxRowsSkipped}` : ''}` +
          `${meta.timeFilteredOut ? `, filtered by date: ${meta.timeFilteredOut}` : ''}. ` +
          `${meta.cexDepositReclassified ? `CEX deposit counterparty reclassified: ${meta.cexDepositReclassified}. ` : ''}` +
          `Use Fetch EUR prices when ready (prices Cronos + Arbitrum rows).`
      );
      if (tokenRegistry?.length) {
        pushLog(
          `Arbitrum: token registry sample — ${tokenRegistry
            .slice(0, 5)
            .map((t) => `${t.tokenSymbol} (${t.contractAddress.slice(0, 8)}…×${t.transferCount})`)
            .join('; ')}${tokenRegistry.length > 5 ? '…' : ''}`
        );
      }
    } catch (err) {
      const msg = String(err?.message || err);
      setArbitrumWarnings([msg]);
      pushLog(`Arbitrum error: ${msg}`);
    } finally {
      setLoadingArbitrum(false);
      setArbitrumMsg('');
    }
  }, [
    arbitrumExplorerApiKey,
    cronosAddr,
    cronosExtraWallets,
    cronosDateFrom,
    cronosDateTo,
    cronosIncomePayerAddresses,
    cexDepositWalletsList,
    pushLog,
  ]);

  const onDexScreenerFilesAdded = useCallback(
    async (e) => {
      const files = e.target.files;
      if (!files?.length) return;
      const readOne = (file) =>
        new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () =>
            resolve(typeof reader.result === 'string' ? reader.result : '');
          reader.onerror = () => reject(reader.error);
          reader.readAsText(file);
        });
      const newEntries = [];
      try {
        for (const f of Array.from(files)) {
          const text = await readOne(f);
          newEntries.push({
            id: newDexScreenerEntryId(),
            filename: f.name,
            symbol: '',
            rawText: text,
          });
        }
      } catch (err) {
        pushLog(`DexScreener: failed to read file(s) — ${String(err?.message || err)}`);
        return;
      }
      setDexScreenerLibrary((prev) => [...prev, ...newEntries]);
      pushLog(
        `DexScreener: added ${newEntries.length} file(s). Set the token symbol for each row, then apply.`
      );
      e.target.value = '';
    },
    [pushLog]
  );

  const removeDexScreenerEntry = useCallback((id) => {
    setDexScreenerLibrary((prev) => prev.filter((x) => x.id !== id));
  }, []);

  const updateDexScreenerEntrySymbol = useCallback((id, symbol) => {
    setDexScreenerLibrary((prev) =>
      prev.map((x) => (x.id === id ? { ...x, symbol } : x))
    );
  }, []);

  const clearDexScreenerLibrary = useCallback(() => {
    setDexScreenerLibrary([]);
    if (dexScreenerFileInputRef.current) dexScreenerFileInputRef.current.value = '';
  }, []);

  const applyDexScreenerToLedger = useCallback(async () => {
    if (!ledgerEvents.length || dexScreenerLibraryApplyReady.length === 0) {
      pushLog(
        'DexScreener: add one or more CSVs and enter each token symbol (as in the ledger) for rows with parsed prices.'
      );
      return;
    }
    setLoadingDexScreenerApply(true);
    try {
      const r = await applyDexScreenerLibraryToLedgerEvents(
        ledgerEvents,
        dexScreenerLibraryApplyReady
      );
      setLedgerEvents(r.events);
      const detail = Object.entries(r.perSymbol)
        .filter(([, n]) => n > 0)
        .map(([s, n]) => `${s}: ${n}`)
        .join('; ');
      pushLog(
        `DexScreener: applied USD→EUR to ${r.applied} Cronos row(s) total${detail ? ` (${detail})` : ''}.`
      );
    } catch (err) {
      pushLog(`DexScreener error: ${String(err?.message || err)}`);
    } finally {
      setLoadingDexScreenerApply(false);
    }
  }, [ledgerEvents, dexScreenerLibraryApplyReady, pushLog]);

  const fetchCronosPrices = useCallback(async () => {
    if (explorerAwaitingPrices === 0) {
      pushLog(
        'Explorer: no rows waiting for EUR — fetch Cronos and/or Arbitrum transactions first (same wallets as below).'
      );
      return;
    }
    setLoadingCronosPrices(true);
    setCronosMsg('');
    setCronosMissingPrices([]);
    pushLog(`Explorer: fetching EUR for ${explorerAwaitingPrices} row(s)…`);
    try {
      let { events, pricedCount, stillMissing, proceedsDerivationStats } =
        await applyExplorerHistoricPrices(ledgerEvents, {
          onProgress: (msg) => {
            setCronosMsg(msg);
            pushLog(`Explorer EUR: ${msg}`);
          },
          ...(ownedWalletsForFifo.length > 0
            ? { ownedWallets: ownedWalletsForFifo }
            : {}),
        });
      if (dexScreenerLibraryApplyReady.length > 0) {
        const dexR = await applyDexScreenerLibraryToLedgerEvents(
          events,
          dexScreenerLibraryApplyReady
        );
        events = dexR.events;
        if (dexR.applied > 0) {
          const detail = Object.entries(dexR.perSymbol)
            .filter(([, n]) => n > 0)
            .map(([s, n]) => `${s}: ${n}`)
            .join('; ');
          pushLog(
            `DexScreener: applied USD→EUR to ${dexR.applied} row(s) after explorer pricing${detail ? ` (${detail})` : ''}.`
          );
        }
      }
      setLedgerEvents(events);
      setCronosMissingPrices(stillMissing);
      pushLog(
        `Explorer EUR: filled ${pricedCount} row(s) (Arbitrum pass + Cronos pipeline); ${stillMissing.length} still without EUR (see list below). Swap-like tx: ${proceedsDerivationStats.swapLikeTxCount}; proceeds from received legs: ${proceedsDerivationStats.disposalProceedsFromReceivedAsset}; outgoing fallback: ${proceedsDerivationStats.disposalProceedsFromOutgoingFallback}; buy-side acq from paid disposals: ${proceedsDerivationStats.tokenAcquisitionsFromPaidDisposals}; zero-proceeds disposals: ${proceedsDerivationStats.zeroProceedsDisposalRows}.`
      );
    } catch (err) {
      const msg = String(err?.message || err);
      setCronosWarnings((w) => [...w, msg]);
      pushLog(`Explorer EUR error: ${msg}`);
    } finally {
      setLoadingCronosPrices(false);
      setCronosMsg('');
    }
  }, [
    explorerAwaitingPrices,
    ledgerEvents,
    ownedWalletsForFifo,
    pushLog,
    dexScreenerLibraryApplyReady,
  ]);

  const onCompiledExplorerFile = useCallback(
    async (e) => {
      const files = e.target.files;
      if (!files?.length) return;
      const file = files[0];
      setReadingCsvFiles(true);
      pushLog(`Compiled explorer: reading ${file.name}…`);
      try {
        const text = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () =>
            resolve(typeof reader.result === 'string' ? reader.result : '');
          reader.onerror = () => reject(reader.error);
          reader.readAsText(file);
        });
        setCompiledExplorerText(text);
        setCompiledExplorerFilesLabel(file.name);
        const lineCount = String(text).split(/\r?\n/).filter((l) => l.trim()).length;
        pushLog(
          `Compiled explorer: ${lineCount} lines (${(String(text).length / 1024).toFixed(1)} KB). Click "Parse & merge into ledger".`
        );
      } catch (err) {
        pushLog(`Compiled explorer read error: ${String(err?.message || err)}`);
        setCompiledExplorerFilesLabel('');
      } finally {
        setReadingCsvFiles(false);
      }
    },
    [pushLog]
  );

  const importCompiledExplorer = useCallback(() => {
    if (!compiledExplorerText.trim()) {
      pushLog('Compiled explorer: paste CSV or choose a file first.');
      return;
    }
    const { wallets, error } = buildOwnedWalletList(cronosAddr, cronosExtraWallets);
    if (error || wallets.length === 0) {
      pushLog(`Compiled explorer: ${error || 'Add at least one owned wallet (section 2).'}`);
      return;
    }
    setLoadingCompiledExplorer(true);
    try {
      const result = parseCompiledExplorerDataset(compiledExplorerText, wallets, {
        incomePayerAddressLines: cronosIncomePayerAddresses.split(/\r?\n/).map((s) => s.trim()).filter(Boolean),
      });
      for (const w of result.warnings) {
        pushLog(`Compiled explorer: ${w}`);
      }
      if (result.fiDiagnostics?.length) {
        setCsvFiDiagnostics((prev) => [...prev, ...result.fiDiagnostics]);
      }
      let events = normalizedLedgerToLedgerEvents(result.ledger);
      if (cexDepositAddressSet.size > 0 && ownedAddressSetForCex.size > 0) {
        const applied = applyCexDepositCounterpartyClassification(events, {
          ownedAddresses: ownedAddressSetForCex,
          cexDepositAddresses: cexDepositAddressSet,
        });
        events = applied.events;
        if (applied.reclassified > 0) {
          pushLog(
            `Compiled explorer: reclassified ${applied.reclassified} row(s) as CEX deposit/withdrawal (non-taxable internal).`
          );
        }
      }
      setLedgerEvents((prev) => mergeEvents(prev, events));
      pushLog(
        `Compiled explorer: merged ${events.length} ledger row(s) from ${result.rawRowCount} raw row(s); ` +
          `token registry: ${result.tokenRegistry.length} contract(s). Unbucketed rows stay visible with warnings.`
      );
    } catch (err) {
      pushLog(`Compiled explorer error: ${String(err?.message || err)}`);
    } finally {
      setLoadingCompiledExplorer(false);
    }
  }, [
    compiledExplorerText,
    cronosAddr,
    cronosExtraWallets,
    cronosIncomePayerAddresses,
    cexDepositAddressSet,
    ownedAddressSetForCex,
    pushLog,
  ]);

  const clearAll = useCallback(() => {
    setLedgerEvents([]);
    setFiatCashEvents([]);
    setCsvSkipped([]);
    setCsvFiDiagnostics([]);
    setCronosWarnings([]);
    setCronosMissingPrices([]);
    setCronosRawExplorer(null);
    setArbitrumWarnings([]);
    setArbitrumRawExplorer(null);
    setCsvText('');
    setCsvFilesLabel('');
    setCompiledExplorerText('');
    setCompiledExplorerFilesLabel('');
    setCronosIncomePayerAddresses('');
    setCronosCexDepositWallets('');
    csvPartHintsRef.current = [];
    setActivityLog([]);
    if (csvFileInputRef.current) csvFileInputRef.current.value = '';
    if (compiledExplorerFileInputRef.current) compiledExplorerFileInputRef.current.value = '';
    if (dexScreenerFileInputRef.current) dexScreenerFileInputRef.current.value = '';
    setDexScreenerLibrary([]);
  }, []);

  const hubHref = getSisuForgeHubHref();
  const hubExternal = isSisuForgeHubExternal(hubHref);

  const hubBrandImages = (
    <>
      <Image
        src={shieldUrl}
        alt=""
        w={{ base: '52px', sm: '56px' }}
        h={{ base: '52px', sm: '56px' }}
        objectFit="contain"
        flexShrink={0}
      />
      <Image
        src={`${PUBLIC}/sisu-forge-wordmark.svg`}
        alt="Sisu Forge"
        maxH={{ base: '32px', sm: '40px' }}
        w="auto"
        maxW="min(100%, 280px)"
        objectFit="contain"
      />
    </>
  );

  return (
    <VStack
      align="stretch"
      spacing={6}
      maxW="min(1400px, 100%)"
      mx="auto"
      py={8}
      px={4}
      color="white"
      bg="blue.800"
    >
      <Box textAlign="center">
        {hubExternal ? (
          <Link
            href={hubHref}
            display="inline-flex"
            alignItems="center"
            justifyContent="center"
            gap={{ base: 3, sm: 4 }}
            flexWrap="wrap"
            _hover={{ opacity: 0.88 }}
            aria-label="Sisu Forge — back to hub"
          >
            {hubBrandImages}
          </Link>
        ) : (
          <Link
            as={RouterLink}
            to={hubHref}
            display="inline-flex"
            alignItems="center"
            justifyContent="center"
            gap={{ base: 3, sm: 4 }}
            flexWrap="wrap"
            _hover={{ opacity: 0.88 }}
            aria-label="Sisu Forge — back to hub"
          >
            {hubBrandImages}
          </Link>
        )}
      </Box>

      <Heading size="lg" textAlign="center">
        Finland crypto tax helper (FIFO, EUR)
      </Heading>

      <Alert status="warning" variant="subtle" borderRadius="md" color="black">
        <AlertIcon />
        <Box>
          This tool is <strong>not</strong> official tax advice. Finland requires
          FIFO for virtual currencies. Verify results and report in{' '}
          <Link href={VERO_URL} isExternal color="blue.600">
            MyTax / form instructions (Vero)
          </Link>
          . CRO/EUR uses bundled daily CRO/USD plus Frankfurter (ECB-based) USD→EUR — outages or
          thin markets can make estimates wrong.
        </Box>
      </Alert>

      <Box bg="blackAlpha.600" p={4} borderRadius="lg">
        <Text fontWeight="bold" mb={2}>
          Reporting period (display)
        </Text>
        <Select
          value={taxPeriod === 'all' ? 'all' : String(taxPeriod)}
          onChange={(e) => {
            const v = e.target.value;
            setTaxPeriod(v === 'all' ? 'all' : Number(v));
          }}
          maxW="280px"
          bg="whiteAlpha.900"
          color="black"
        >
          <option value="all">All time (all disposals)</option>
          {yearsOptions().map((y) => (
            <option key={y} value={y}>
              Calendar year {y} (UTC)
            </option>
          ))}
        </Select>
        <Text fontSize="sm" mt={2} opacity={0.9}>
          FIFO always uses <strong>all</strong> imported events. Choose a calendar year for
          Finnish annual reporting, or <strong>All time</strong> to see every realised gain/loss
          in one table.
        </Text>
        <Text fontSize="xs" mt={3} opacity={0.88} maxW="720px">
          <strong>Filing basis</strong> is set in <code>taxPipelineConfig.fi.basisMethod</code> (default{' '}
          <code>best</code>): for each disposal the CSV “chosen” columns use the better of{' '}
          <strong>FIFO</strong> gain/loss and <strong>deemed</strong> gain/loss (deemed acquisition cost =
          20% of proceeds if holding &lt;10y or unknown, 40% if ≥10y). This page clears{' '}
          <code>sisuforge.fi.*</code> localStorage on load so behaviour does not depend on old browser prefs.
        </Text>
      </Box>

      <Box bg="blackAlpha.600" p={4} borderRadius="lg">
        <Heading size="sm" mb={3}>
          1. Exchange CSV (Crypto.com, Binance, Coinbase)
        </Heading>
        <Text fontSize="sm" mb={2}>
          The importer <strong>detects the exchange</strong> from each file’s headers (not from the filename).
          Select <strong>multiple files at once</strong> when you have several exports — each file is parsed with
          its own header row (no manual merging).
        </Text>
        <Box
          fontSize="xs"
          mb={3}
          p={3}
          borderRadius="md"
          bg="blackAlpha.500"
          borderWidth="1px"
          borderColor="whiteAlpha.300"
        >
          <Text fontWeight="bold" mb={1}>
            Binance — upload both:
          </Text>
          <Text mb={2}>
            (1) <strong>Transaction Records / Asset History</strong> export, and (2){' '}
            <strong>Spot Trade History</strong>. If Binance offers “information to be included” or similar, choose the{' '}
            <strong>most detailed / all available information</strong>. Earn / Simple Earn may need extra exports
            later — this version focuses on transaction + spot trade CSVs.
          </Text>
          <Text fontWeight="bold" mb={1}>
            Coinbase — upload:
          </Text>
          <Text mb={2}>
            (1) <strong>Raw Transaction History / Custom Report</strong> from Coinbase Taxes. If you used{' '}
            <strong>Coinbase Exchange / old Coinbase Pro</strong>, add <strong>Accounts</strong> and{' '}
            <strong>Fills</strong> statements when available. <strong>Coinbase Wallet</strong> is separate and is not
            in Coinbase Taxes — use chain data (Cronos section) where applicable. Account balance CSVs are accepted
            for detection but are <strong>not</strong> mapped to FIFO in this version (diagnostic only).
          </Text>
          <Text fontWeight="bold" mb={1}>
            Crypto.com:
          </Text>
          <Text>
            Same as before: app or Exchange transaction CSV with timestamps, kinds, amounts, and EUR/USD columns
            when possible.
          </Text>
        </Box>
        <Input
          ref={csvFileInputRef}
          type="file"
          accept=".csv,text/csv"
          multiple
          onChange={onFile}
          mb={2}
        />
        {csvFilesLabel && (
          <Text fontSize="xs" mb={2} opacity={0.85}>
            Loaded: {csvFilesLabel}
          </Text>
        )}
        <TextareaMini value={csvText} onChange={setCsvText} />

        {csvBusy && (
          <HStack
            mt={3}
            p={4}
            borderRadius="lg"
            bg="orange.400"
            color="gray.900"
            spacing={4}
            justify="center"
            boxShadow="lg"
          >
            <Spinner size="xl" thickness="5px" speed="0.65s" emptyColor="blackAlpha.300" />
            <VStack align="start" spacing={1}>
              <Text fontWeight="extrabold" fontSize="md">
                {readingCsvFiles ? 'Reading your CSV files…' : 'Parsing CSV & fetching FX rates…'}
              </Text>
              <Text fontSize="sm" fontWeight="medium">
                {readingCsvFiles
                  ? 'Watch the status log below.'
                  : 'Many USD→EUR lookups take time. Use Stop to merge partial results. Watch the status log below.'}
              </Text>
            </VStack>
          </HStack>
        )}

        <Text fontSize="sm" mt={3} fontWeight="semibold" color="orange.100">
          After files load into the box above, run the import:
        </Text>
        <HStack mt={2} w="100%" spacing={3} align="stretch">
          <Button
            flex={1}
            minW={0}
            size="lg"
            py={7}
            fontSize="lg"
            fontWeight="bold"
            colorScheme="orange"
            onClick={importCsv}
            isLoading={loadingCsv}
            loadingText="Parsing…"
            spinnerPlacement="start"
            isDisabled={csvBusy}
          >
            Parse &amp; merge into ledger
          </Button>
          {loadingCsv && (
            <Button
              size="lg"
              px={5}
              py={7}
              fontWeight="bold"
              colorScheme="red"
              variant="solid"
              flexShrink={0}
              onClick={stopCsvParse}
            >
              Stop
            </Button>
          )}
        </HStack>

        <Box
          mt={3}
          bg="blackAlpha.800"
          borderWidth="1px"
          borderColor="whiteAlpha.400"
          borderRadius="md"
          p={3}
          maxH="220px"
          overflowY="auto"
        >
          <HStack justify="space-between" mb={2} flexWrap="wrap" gap={2}>
            <Text fontSize="xs" fontWeight="bold" letterSpacing="wide" color="orange.200">
              STATUS LOG
            </Text>
            <HStack spacing={2}>
              {activityLog.length > 0 ? (
                <Button
                  size="xs"
                  variant="outline"
                  colorScheme="orange"
                  onClick={() => setShowActivityLog((v) => !v)}
                >
                  {showActivityLog ? 'Hide log' : 'Show log'}
                </Button>
              ) : null}
              <Button size="xs" variant="ghost" color="whiteAlpha.800" onClick={() => setActivityLog([])}>
                Clear log
              </Button>
            </HStack>
          </HStack>
          {activityLog.length === 0 ? (
            <Text fontSize="xs" opacity={0.65} fontStyle="italic">
              File reads, parse progress, and Cronos steps will appear here so you know the app is
              working.
            </Text>
          ) : !showActivityLog ? (
            <Text fontSize="xs" opacity={0.85}>
              <strong>{activityLog.length}</strong> line(s) — click <strong>Show log</strong> to expand (hidden by
              default to keep the page light).
            </Text>
          ) : (
            <VStack align="stretch" spacing={1} fontFamily="mono" fontSize="11px">
              {activityLog.map((line, i) => (
                <Text key={i} whiteSpace="pre-wrap" wordBreak="break-word">
                  {line}
                </Text>
              ))}
            </VStack>
          )}
        </Box>
        {csvSkipped.length > 0 && (
          <Box mt={3}>
            <HStack justify="space-between" align="center" mb={1} flexWrap="wrap" gap={2}>
              <Text fontWeight="bold" fontSize="xs">
                CSV notes / skipped rows ({csvSkipped.length})
              </Text>
              <HStack spacing={2} flexWrap="wrap">
                <Button
                  size="xs"
                  variant="outline"
                  colorScheme="orange"
                  onClick={() => setShowCsvSkippedPanel((v) => !v)}
                >
                  {showCsvSkippedPanel ? 'Hide details' : 'Show details'}
                </Button>
                <Button
                  size="xs"
                  colorScheme="orange"
                  variant="outline"
                  onClick={() => {
                    const t = csvSkipped.join('\n');
                    void navigator.clipboard?.writeText(t);
                  }}
                >
                  Copy all
                </Button>
              </HStack>
            </HStack>
            {showCsvSkippedPanel ? (
              <Textarea
                readOnly
                value={csvSkipped.join('\n')}
                fontSize="xs"
                fontFamily="mono"
                minH="100px"
                maxH="240px"
                overflowY="auto"
                bg="blackAlpha.700"
                color="white"
                borderColor="whiteAlpha.400"
                whiteSpace="pre-wrap"
                wordBreak="break-word"
                cursor="text"
                sx={{
                  userSelect: 'text',
                  WebkitUserSelect: 'text',
                }}
                _focus={{ borderColor: 'orange.300', boxShadow: 'outline' }}
              />
            ) : (
              <Text fontSize="xs" opacity={0.8}>
                Details hidden by default — <strong>Show details</strong> to view or copy line-by-line.
              </Text>
            )}
          </Box>
        )}
      </Box>

      <Box bg="blackAlpha.600" p={4} borderRadius="lg">
        <Heading size="sm" mb={3}>
          2. On-chain — Cronos &amp; Arbitrum (wallets &amp; explorer)
        </Heading>
        <Text fontSize="sm" mb={2}>
          Add every address <strong>you control</strong> (self-custody). Transfers <strong>between</strong> those
          addresses are non-taxable <strong>internal</strong> moves (FIFO skips them). Do <strong>not</strong> add
          exchange deposit hot wallets here — use the separate field below. Fetch loads explorer data only;{' '}
          <strong>Fetch EUR prices</strong> fills EUR for both Cronos and Arbitrum rows. Optional dates filter results{' '}
          <strong>after</strong> fetch (shared by Cronos and Arbitrum).
        </Text>
        <Text fontSize="xs" mb={1} opacity={0.9}>
          Cronos Explorer API key (required){' '}
          <Link
            href="https://docs.cronos.org/block-explorers/block-explorer-and-api-keys"
            isExternal
            color="blue.300"
            textDecoration="underline"
          >
            get a key
          </Link>
        </Text>
        <Input
          type="password"
          autoComplete="off"
          mb={3}
          placeholder="Paste API key, or use REACT_APP_CRONOS_EXPLORER_API_KEY in .env"
          value={cronosExplorerApiKey}
          onChange={(e) => setCronosExplorerApiKey(e.target.value)}
          bg="whiteAlpha.900"
          color="black"
        />
        <Text fontSize="xs" mb={1} opacity={0.9}>
          Primary wallet (required)
        </Text>
        <Input
          mb={3}
          placeholder="0x… primary Cronos wallet"
          value={cronosAddr}
          onChange={(e) => setCronosAddr(e.target.value)}
          bg="whiteAlpha.900"
          color="black"
        />
        <Text fontSize="xs" mb={1} opacity={0.9}>
          Additional owned addresses (optional), one per line — <code>Label|0x…</code> or <code>0x…</code>
        </Text>
        <Textarea
          mb={3}
          rows={3}
          placeholder={'DeFi|0x…\nLedger|0x…'}
          value={cronosExtraWallets}
          onChange={(e) => setCronosExtraWallets(e.target.value)}
          bg="whiteAlpha.900"
          color="black"
          fontSize="sm"
        />
        <Text fontSize="xs" mb={1} opacity={0.9}>
          CEX deposit addresses (optional), one per line — <code>Label|0x…</code> or <code>0x…</code>. These are{' '}
          <strong>not</strong> fetched from the explorer (shared hot wallets). Simple transfers between your owned
          address and one of these are reclassified as non-taxable internal moves (Finnish deposit/withdrawal to
          your own exchange account — confirm with Vero). Does not apply to swaps or other complex txs.
        </Text>
        <Textarea
          mb={3}
          rows={2}
          placeholder={'Crypto.com deposit|0x4d38dB8B08c3C6AD1243f46440B743AC55869069'}
          value={cronosCexDepositWallets}
          onChange={(e) => setCronosCexDepositWallets(e.target.value)}
          bg="whiteAlpha.900"
          color="black"
          fontSize="sm"
        />
        <Text fontSize="xs" mb={1} opacity={0.9}>
          Income / reward payer contracts (optional), one per line — <code>0x…</code> or <code>Label|0x…</code>.
          Incoming ERC-20 or native CRO <strong>from</strong> these addresses to an owned wallet is classified as
          taxable income (after EUR pricing), not a purchase. Merged with any defaults in code. Re-fetch Cronos or
          re-parse compiled explorer after editing.
        </Text>
        <Textarea
          mb={3}
          rows={2}
          placeholder={'0x… staking contract\nNFT|0x…'}
          value={cronosIncomePayerAddresses}
          onChange={(e) => setCronosIncomePayerAddresses(e.target.value)}
          bg="whiteAlpha.900"
          color="black"
          fontSize="sm"
        />
        <HStack flexWrap="wrap" spacing={4} mb={3} align="flex-end">
          <Box>
            <Text fontSize="xs" mb={1} opacity={0.9}>
              From (UTC date, optional)
            </Text>
            <Input
              type="date"
              value={cronosDateFrom}
              onChange={(e) => setCronosDateFrom(e.target.value)}
              bg="whiteAlpha.900"
              color="black"
              maxW="200px"
            />
          </Box>
          <Box>
            <Text fontSize="xs" mb={1} opacity={0.9}>
              To (UTC date, optional)
            </Text>
            <Input
              type="date"
              value={cronosDateTo}
              onChange={(e) => setCronosDateTo(e.target.value)}
              bg="whiteAlpha.900"
              color="black"
              maxW="200px"
            />
          </Box>
        </HStack>
        <HStack flexWrap="wrap" spacing={3}>
          <Button
            colorScheme="teal"
            size="lg"
            minW="200px"
            onClick={fetchCronosTransactions}
            isLoading={loadingCronos}
            loadingText="Explorer…"
            spinnerPlacement="start"
            isDisabled={loadingCronos || loadingCronosPrices || loadingArbitrum}
          >
            Fetch Cronos transactions
          </Button>
          <Button
            colorScheme="orange"
            size="lg"
            minW="200px"
            onClick={fetchCronosPrices}
            isLoading={loadingCronosPrices}
            loadingText="Pricing…"
            spinnerPlacement="start"
            isDisabled={
              loadingCronos ||
              loadingCronosPrices ||
              loadingArbitrum ||
              explorerAwaitingPrices === 0
            }
          >
            Fetch EUR prices
          </Button>
        </HStack>
        <Box
          mt={4}
          p={3}
          borderRadius="md"
          bg="blackAlpha.500"
          borderWidth="1px"
          borderColor="whiteAlpha.300"
        >
          <Text fontWeight="bold" fontSize="sm" mb={2}>
            DexScreener export (optional token USD prices)
          </Text>
          <Text fontSize="xs" mb={2} opacity={0.9}>
            Add <strong>one CSV per token</strong> (or select several files at once). Each row needs the{' '}
            <strong>ledger symbol</strong> (e.g. BUTT, FRTN). Rows match by <strong>transaction hash</strong> in the
            explorer link column. Price prefers <strong>USD total ÷ token amount</strong> when both exist; otherwise{' '}
            <strong>mantissa × 10<sup>−exp</sup></strong>. If the CSV has a <strong>DATE / USD / …</strong> header,
            columns are matched by name (so <strong>WCRO</strong> is not used as USD). For Chakra-only headers, layout
            must match the classic scrape. Dates without a year use the <strong>reporting period year</strong> —{' '}
            re-add files after changing that year if needed.
          </Text>
          <HStack flexWrap="wrap" spacing={3} align="flex-end" mb={3}>
            <Box>
              <Text fontSize="xs" mb={1} opacity={0.9}>
                Add CSV (multi-select)
              </Text>
              <Input
                ref={dexScreenerFileInputRef}
                type="file"
                accept=".csv,text/csv"
                multiple
                onChange={(e) => {
                  void onDexScreenerFilesAdded(e);
                }}
                bg="whiteAlpha.900"
                color="black"
                maxW="360px"
              />
            </Box>
            <Button size="sm" variant="outline" onClick={clearDexScreenerLibrary}>
              Clear DexScreener library
            </Button>
            <Button
              size="sm"
              colorScheme="cyan"
              onClick={() => {
                void applyDexScreenerToLedger();
              }}
              isLoading={loadingDexScreenerApply}
              isDisabled={
                dexScreenerLibraryApplyReady.length === 0 || !ledgerEvents.length
              }
            >
              Apply DexScreener prices to ledger
            </Button>
          </HStack>
          {dexScreenerParsedLibrary.length > 0 ? (
            <Table size="sm" variant="simple" sx={{ th: { color: 'whiteAlpha.800' } }}>
              <Thead>
                <Tr>
                  <Th>File</Th>
                  <Th>Token symbol</Th>
                  <Th isNumeric>Tx prices</Th>
                  <Th />
                </Tr>
              </Thead>
              <Tbody>
                {dexScreenerParsedLibrary.map((row) => (
                  <Tr key={row.id}>
                    <Td maxW="220px" fontSize="xs" whiteSpace="nowrap" overflow="hidden" textOverflow="ellipsis" title={row.filename}>
                      {row.filename}
                    </Td>
                    <Td>
                      <Input
                        size="sm"
                        placeholder="e.g. BUTT"
                        value={row.symbol}
                        onChange={(e) =>
                          updateDexScreenerEntrySymbol(row.id, e.target.value)
                        }
                        bg="whiteAlpha.900"
                        color="black"
                        maxW="140px"
                      />
                    </Td>
                    <Td isNumeric fontSize="xs">
                      {row.parsed.rows.length}
                    </Td>
                    <Td textAlign="right">
                      <Button
                        size="xs"
                        variant="ghost"
                        colorScheme="red"
                        onClick={() => removeDexScreenerEntry(row.id)}
                      >
                        Remove
                      </Button>
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          ) : (
            <Text fontSize="xs" opacity={0.8}>
              No DexScreener files in the library yet.
            </Text>
          )}
        </Box>
        <Text fontSize="xs" mt={2} opacity={0.9}>
          Explorer rows waiting for EUR (Cronos + Arbitrum): <strong>{explorerAwaitingPrices}</strong>
        </Text>
        {cronosMsg && (
          <Text fontSize="sm" mt={2}>
            {cronosMsg}
          </Text>
        )}
        {cronosWarnings.length > 0 && (
          <Box mt={2} fontSize="xs" maxH="100px" overflowY="auto">
            {cronosWarnings.slice(0, 20).map((w, i) => (
              <Text key={i} opacity={0.85}>
                {w}
              </Text>
            ))}
          </Box>
        )}
        <HStack mt={3} flexWrap="wrap" gap={2} align="center">
          <Button
            size="sm"
            colorScheme="purple"
            variant="solid"
            onClick={exportCronosRawExplorerCsv}
            isDisabled={
              !cronosRawExplorer ||
              (!cronosRawExplorer.tokenTransfers?.length &&
                !cronosRawExplorer.nativeTransactions?.length &&
                !cronosRawExplorer.internalTransactions?.length)
            }
          >
            Export raw Cronos explorer (CSV)
          </Button>
          <Text fontSize="xs" opacity={0.85} maxW="lg">
            Combined <code>tokentx</code> + <code>txlist</code> rows with every API field preserved
            (deduplicated). Column <code>explorer_record_type</code> marks the source.
          </Text>
        </HStack>

        {cronosMissingPrices.length > 0 && (
          <Box mt={3}>
            <HStack justify="space-between" align="center" mb={1} flexWrap="wrap" gap={2}>
              <Text fontWeight="bold" fontSize="xs">
                Explorer movements still without EUR ({cronosMissingPrices.length})
              </Text>
              <HStack spacing={2} flexWrap="wrap">
                <Button
                  size="xs"
                  variant="outline"
                  colorScheme="orange"
                  onClick={() => setShowExplorerMissingPanel((v) => !v)}
                >
                  {showExplorerMissingPanel ? 'Hide list' : 'Show list'}
                </Button>
                <Button
                  size="xs"
                  colorScheme="orange"
                  variant="outline"
                  onClick={() => {
                    void navigator.clipboard?.writeText(cronosMissingPrices.join('\n'));
                  }}
                >
                  Copy all
                </Button>
              </HStack>
            </HStack>
            {showExplorerMissingPanel ? (
              <Textarea
                readOnly
                value={cronosMissingPrices.join('\n')}
                fontSize="xs"
                fontFamily="mono"
                minH="100px"
                maxH="240px"
                overflowY="auto"
                bg="blackAlpha.700"
                color="white"
                borderColor="whiteAlpha.400"
                whiteSpace="pre-wrap"
                wordBreak="break-word"
                cursor="text"
                sx={{
                  userSelect: 'text',
                  WebkitUserSelect: 'text',
                }}
                _focus={{ borderColor: 'orange.300', boxShadow: 'outline' }}
              />
            ) : (
              <Text fontSize="xs" opacity={0.8}>
                List hidden by default — <strong>Show list</strong> to inspect date | kind | asset | ref.
              </Text>
            )}
          </Box>
        )}

        <Divider my={5} borderColor="whiteAlpha.300" />

        <Heading size="xs" mb={2} textTransform="uppercase" letterSpacing="wide" opacity={0.95}>
          Arbitrum One (Arbiscan)
        </Heading>
        <Text fontSize="sm" mb={2}>
          Uses the <strong>same owned wallets, UTC date range, CEX deposit list, and income payer contracts</strong>{' '}
          as Cronos above. Arbitrum uses <strong>Etherscan API V2</strong> (<code>api.etherscan.io/v2/api</code> with{' '}
          <code>chainid=42161</code>) — legacy <code>api.arbiscan.io</code> v1 returns NOTOK. Create a key in your{' '}
          <Link href="https://etherscan.io/apidashboard" isExternal color="blue.300" textDecoration="underline">
            Etherscan API dashboard
          </Link>
          .
        </Text>
        <Text fontSize="xs" mb={1} opacity={0.9}>
          Etherscan API V2 key (required for live fetch)
        </Text>
        <Input
          type="password"
          autoComplete="off"
          mb={3}
          placeholder="Paste Etherscan API V2 key, or REACT_APP_ARBISCAN_API_KEY / REACT_APP_ETHERSCAN_API_KEY in .env"
          value={arbitrumExplorerApiKey}
          onChange={(e) => setArbitrumExplorerApiKey(e.target.value)}
          bg="whiteAlpha.900"
          color="black"
        />
        <HStack flexWrap="wrap" spacing={3} mb={2}>
          <Button
            colorScheme="teal"
            size="md"
            minW="220px"
            onClick={fetchArbitrumTransactions}
            isLoading={loadingArbitrum}
            loadingText="Arbiscan…"
            spinnerPlacement="start"
            isDisabled={loadingCronos || loadingCronosPrices || loadingArbitrum}
          >
            Fetch Arbitrum transactions
          </Button>
        </HStack>
        {arbitrumMsg && (
          <Text fontSize="sm" mt={1}>
            {arbitrumMsg}
          </Text>
        )}
        {arbitrumWarnings.length > 0 && (
          <Box mt={2} fontSize="xs" maxH="100px" overflowY="auto">
            {arbitrumWarnings.slice(0, 20).map((w, i) => (
              <Text key={i} opacity={0.85}>
                {w}
              </Text>
            ))}
          </Box>
        )}
        <HStack mt={3} flexWrap="wrap" gap={2} align="center">
          <Button
            size="sm"
            colorScheme="purple"
            variant="solid"
            onClick={exportArbitrumRawExplorerCsv}
            isDisabled={
              !arbitrumRawExplorer ||
              (!arbitrumRawExplorer.tokenTransfers?.length &&
                !arbitrumRawExplorer.nativeTransactions?.length &&
                !arbitrumRawExplorer.internalTransactions?.length)
            }
          >
            Export raw Arbitrum explorer (CSV)
          </Button>
          <Text fontSize="xs" opacity={0.85} maxW="lg">
            Same combined <code>tokentx</code> + <code>txlist</code> + <code>internal</code> shape as the Cronos export.
          </Text>
        </HStack>
      </Box>

      <Box bg="blackAlpha.600" p={4} borderRadius="lg">
        <Heading size="sm" mb={3}>
          3. Compiled explorer CSV (mixed txlist / tokentx / internal)
        </Heading>
        <Text fontSize="sm" mb={2}>
          Import one table that combines <code>txlist</code>, <code>tokentx</code>, and{' '}
          <code>txlistinternal</code> with a column <code>explorer_record_type</code> (same shape as the
          export in step 2). Parsing is driven by that column; rows are grouped by <code>hash</code> before
          classification. Uses the <strong>same owned wallets</strong> as section 2 — fill those first.
        </Text>
        <Input
          ref={compiledExplorerFileInputRef}
          type="file"
          accept=".csv,text/csv"
          onChange={onCompiledExplorerFile}
          mb={2}
        />
        {compiledExplorerFilesLabel && (
          <Text fontSize="xs" mb={2} opacity={0.85}>
            Loaded: {compiledExplorerFilesLabel}
          </Text>
        )}
        <Text fontSize="xs" mb={1} opacity={0.9}>
          Or paste CSV below
        </Text>
        <TextareaMini
          value={compiledExplorerText}
          onChange={setCompiledExplorerText}
          placeholder="explorer_record_type,hash,timeStamp,…"
          rows={5}
          minH="120px"
        />
        <Button
          mt={3}
          colorScheme="cyan"
          size="md"
          onClick={importCompiledExplorer}
          isLoading={loadingCompiledExplorer}
          loadingText="Parsing…"
          isDisabled={
            loadingCompiledExplorer || loadingCronos || loadingCronosPrices || loadingArbitrum
          }
        >
          Parse compiled explorer &amp; merge into ledger
        </Button>
        <Text fontSize="xs" mt={2} opacity={0.85}>
          Taxable legs get the same EUR pricing hints as live explorer imports — use <strong>Fetch EUR prices</strong>{' '}
          in step 2 after merging.
        </Text>
      </Box>

      <HStack flexWrap="wrap" gap={2}>
        <Button size="sm" variant="outline" onClick={clearAll}>
          Clear ledger
        </Button>
        <Text fontSize="sm">
          Events in ledger: <strong>{ledgerEvents.length}</strong>
          {explorerAwaitingPrices > 0 ? (
            <>
              {' '}
              (<strong>{explorerAwaitingPrices}</strong> explorer row(s) without EUR)
            </>
          ) : null}
          {internalTransferCount > 0 ? (
            <>
              {' '}
              · <strong>{internalTransferCount}</strong> internal (non-tax)
            </>
          ) : null}
        </Text>
      </HStack>

      <Divider />

      {ledgerEvents.length > 0 && (
        <Box bg="blackAlpha.500" p={4} borderRadius="lg" borderWidth="1px" borderColor="whiteAlpha.300">
          <HStack justify="space-between" align="flex-start" mb={2} flexWrap="wrap" gap={2}>
            <Box>
              <HStack align="center" flexWrap="wrap" gap={2}>
                <Heading size="sm">Full ledger</Heading>
                <Button
                  size="xs"
                  variant="outline"
                  colorScheme="cyan"
                  onClick={() => setShowFullLedgerTable((v) => !v)}
                >
                  {showFullLedgerTable ? 'Hide table' : 'Show ledger table'}
                </Button>
              </HStack>
              <Text fontSize="xs" opacity={0.85} mt={1}>
                Every imported row (Crypto.com + Cronos + Arbitrum + compiled explorer), including{' '}
                <strong>no EUR yet</strong>. FIFO summary below only lists <strong>taxable disposals</strong> after
                matching — unpriced explorer rows stay here until you fetch EUR prices. The table is{' '}
                <strong>hidden by default</strong> — use exports above or <strong>Show ledger table</strong> to browse.
              </Text>
            </Box>
            <HStack flexWrap="wrap" gap={2}>
              <Button size="sm" colorScheme="cyan" variant="solid" onClick={exportFullLedgerCsv}>
                Export full ledger CSV
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={exportFiDiagnosticsCsv}
                isDisabled={!csvFiDiagnostics.length}
              >
                Export FI diagnostics CSV ({csvFiDiagnostics.length})
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={exportFiIncomeAllTimeCsv}
                isDisabled={!incomeLedgerRows.length}
              >
                Export FI income CSV ({incomeLedgerRows.length})
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={exportFiIncomePipelineDiagnosticsCsv}
                isDisabled={!ledgerEvents.length}
              >
                Export FI income diagnostics
              </Button>
            </HStack>
          </HStack>
          <HStack mb={2} flexWrap="wrap" gap={2} fontSize="sm">
            <Text>
              Total rows: <strong>{sortedLedgerEvents.length}</strong>
            </Text>
            {sortedLedgerEvents.length > LEDGER_PAGE_SIZE ? (
              <>
                <Text>
                  Page <strong>{ledgerPageSafe}</strong> / {ledgerPageCount} ({LEDGER_PAGE_SIZE} per page)
                </Text>
                <Button
                  size="xs"
                  variant="outline"
                  onClick={() => setLedgerPage((p) => Math.max(1, p - 1))}
                  isDisabled={ledgerPageSafe <= 1}
                >
                  Previous
                </Button>
                <Button
                  size="xs"
                  variant="outline"
                  onClick={() => setLedgerPage((p) => Math.min(ledgerPageCount, p + 1))}
                  isDisabled={ledgerPageSafe >= ledgerPageCount}
                >
                  Next
                </Button>
              </>
            ) : null}
          </HStack>
          {!showFullLedgerTable ? (
            <Text fontSize="sm" opacity={0.88} mt={1}>
              Ledger table is not shown — <strong>{sortedLedgerEvents.length}</strong> row(s). Use{' '}
              <strong>Show ledger table</strong> to paginate in the browser.
            </Text>
          ) : (
            <Box
              overflowX="auto"
              maxH="min(55vh, 520px)"
              overflowY="auto"
              borderWidth="1px"
              borderColor="whiteAlpha.300"
              borderRadius="md"
            >
              <Table size="sm" variant="simple">
                <Thead>
                  <Tr>
                    <Th color="gray.200">#</Th>
                    <Th color="gray.200">UTC</Th>
                    <Th color="gray.200">Source</Th>
                    <Th color="gray.200">Kind</Th>
                    <Th color="gray.200">Asset</Th>
                    <Th color="gray.200" isNumeric>
                      Amount
                    </Th>
                    <Th color="gray.200" isNumeric>
                      EUR
                    </Th>
                    <Th color="gray.200">Class</Th>
                    <Th color="gray.200">FI</Th>
                    <Th color="gray.200">Ref</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {ledgerPageSlice.map((e, i) => {
                    const iso = new Date(e.ts).toISOString();
                    const globalIx = (ledgerPageSafe - 1) * LEDGER_PAGE_SIZE + i + 1;
                    const amt = e.amount?.toFixed != null ? e.amount.toFixed(8) : String(e.amount);
                    const eur =
                      e.eurValue != null && e.eurValue.toFixed != null ? (
                        `€${e.eurValue.toFixed(2)}`
                      ) : (
                        <Text as="span" color="orange.200">
                          —
                        </Text>
                      );
                    return (
                      <Tr key={`${e.ts}-${e.source}-${e.kind}-${e.asset}-${e.ref ?? i}-${globalIx}`}>
                        <Td color="gray.400" fontSize="xs">
                          {globalIx}
                        </Td>
                        <Td fontSize="xs" fontFamily="mono" whiteSpace="nowrap">
                          {iso.slice(0, 10)} {iso.slice(11, 19)}
                        </Td>
                        <Td fontSize="xs">{e.source}</Td>
                        <Td fontSize="xs">{e.kind}</Td>
                        <Td fontSize="xs" fontWeight="medium">
                          {e.asset}
                        </Td>
                        <Td isNumeric fontSize="xs" fontFamily="mono">
                          {amt}
                        </Td>
                        <Td isNumeric fontSize="xs" fontFamily="mono">
                          {eur}
                        </Td>
                        <Td fontSize="xs" maxW="120px" whiteSpace="normal" wordBreak="break-word">
                          {e.classification ?? '—'}
                        </Td>
                        <Td fontSize="xs" maxW="100px" title={e.fiSubreason || undefined}>
                          {e.fiTaxCategory ?? '—'}
                        </Td>
                        <Td
                          fontSize="xs"
                          fontFamily="mono"
                          maxW="140px"
                          wordBreak="break-all"
                          title={e.ref || undefined}
                        >
                          {e.ref ? `${e.ref.slice(0, 12)}…` : '—'}
                        </Td>
                      </Tr>
                    );
                  })}
                </Tbody>
              </Table>
            </Box>
          )}
        </Box>
      )}

      {ledgerEvents.length > 0 && lotRepairLoading && (
        <HStack mb={3} spacing={3}>
          <Spinner size="sm" />
          <Text fontSize="sm">
            Running FIFO pass 1 + lot basis repair + FIFO pass 2 (final figures below)…
          </Text>
        </HStack>
      )}

      {fifoDisplay && (
        <Box>
          <HStack
            justify="space-between"
            align="flex-start"
            mb={3}
            flexWrap="wrap"
            gap={3}
          >
            <Heading size="md">
              Summary —{' '}
              {taxPeriod === 'all'
                ? 'all time (every disposal)'
                : `${taxPeriod} (disposals in this calendar year, UTC)`}
            </Heading>
            <HStack flexWrap="wrap" gap={2}>
              <Button
                size="sm"
                colorScheme="green"
                variant="solid"
                onClick={() => {
                  void exportFinnishCapitalGainsCsv();
                }}
                isDisabled={
                  disposalDetailsForView.length === 0 ||
                  lotRepairLoading ||
                  capitalGainsExporting
                }
                isLoading={capitalGainsExporting}
              >
                Export capital gains (CSV)
              </Button>
              <Button
                size="sm"
                colorScheme="orange"
                variant="outline"
                onClick={exportDisposalsCsv}
                isDisabled={disposalDetailsForView.length === 0 || lotRepairLoading}
              >
                Export disposals (extended CSV)
              </Button>
              <Button
                size="sm"
                colorScheme="purple"
                variant="outline"
                onClick={exportLotBasisRepairDiagnosticsCsv}
                isDisabled={!lotRepairPipeline?.stats || lotRepairLoading}
              >
                Export lot basis repair diagnostics (CSV)
              </Button>
              <Button
                size="sm"
                colorScheme="cyan"
                variant="outline"
                onClick={exportFiFiatReconciliationCsv}
                isDisabled={fiatCashEvents.length === 0}
              >
                Export FI fiat reconciliation (CSV)
              </Button>
              <Button
                size="sm"
                colorScheme="cyan"
                variant="ghost"
                onClick={exportFiFiatSummaryCsv}
                isDisabled={fiatCashEvents.length === 0}
              >
                Export FI fiat summary (CSV)
              </Button>
            </HStack>
          </HStack>
          <Text fontSize="xs" opacity={0.85} mb={3}>
            <strong>Capital gains CSV</strong> includes policy metadata,{' '}
            <code>filing_basis_audit_note</code> (auditable when basis_method is best), FIFO / deemed /
            chosen amounts, and the compact Vero-style columns.{' '}
            <strong>Extended CSV</strong> repeats the same basis columns plus Crypto.com fields. Table matches
            the reporting period; click headers to sort before export.{' '}
            <strong>Lot basis repair</strong> (pass 2) fills missing acquisition/income EUR where rules allow;
            diagnostics CSV summarizes repairs vs unresolved.{' '}
            <strong>Crypto.com</strong> rows with the same reference can appear as several FIFO lot slices in the
            engine; here they are merged into one line (column <code>fifo_merged_lot_slices</code> in the extended
            CSV when the value is greater than 1). Capital gain/loss totals use the full FIFO disposal detail;{' '}
            <strong>taxable income</strong> (rewards, staking, etc.) is summed separately below and is not part of
            capital net.
          </Text>
          {lotRepairPipeline?.stats && (
            <Text fontSize="xs" opacity={0.9} mb={2}>
              Lot repair: <strong>{lotRepairPipeline.stats.repairedTotal}</strong> lot(s) repaired ·{' '}
              <strong>{lotRepairPipeline.stats.syntheticStableInventoryBackfills}</strong> synthetic stable lot(s)
              (missing inventory) · <strong>{lotRepairPipeline.stats.unresolvedCandidates}</strong> still zero-basis
              (review) · Δ FIFO
              gain €{lotRepairPipeline.stats.deltaFifoGainEur.toFixed(2)} · Δ filing gain €
              {lotRepairPipeline.stats.deltaChosenGainEur.toFixed(2)}
            </Text>
          )}
          <VStack align="stretch" spacing={3} mb={4}>
            <Text fontSize="xs" fontWeight="semibold" opacity={0.92} textTransform="uppercase" letterSpacing="wider">
              Capital gains and losses (disposals only)
            </Text>
            <HStack spacing={8} flexWrap="wrap">
              <Stat
                label={
                  TAX_PIPELINE.fi.basisMethod === 'fifo'
                    ? 'Capital gains (FIFO)'
                    : 'Capital gains (filing, chosen)'
                }
                value={`€${fifoDisplay.totalGainEur.toFixed(2)}`}
              />
              <Stat
                label={
                  TAX_PIPELINE.fi.basisMethod === 'fifo'
                    ? 'Capital losses (abs, FIFO)'
                    : 'Capital losses (abs, filing)'
                }
                value={`€${fifoDisplay.totalLossEur.toFixed(2)}`}
              />
              <Stat
                label="Capital net (gains − losses)"
                value={`€${fifoDisplay.totalGainEur.minus(fifoDisplay.totalLossEur).toFixed(2)}`}
              />
              <Stat label="Disposal rows (this view)" value={String(disposalDetailsForView.length)} />
              <Stat
                label="Disposal rows flagged (review)"
                value={String(problemIndices.length)}
              />
            </HStack>
            <Text fontSize="xs" fontWeight="semibold" opacity={0.92} textTransform="uppercase" letterSpacing="wider" pt={1}>
              Taxable income (FMV at receipt — separate from capital)
            </Text>
            <HStack spacing={8} flexWrap="wrap">
              <Stat
                label={
                  taxPeriod === 'all'
                    ? 'Income FMV (all time, filing)'
                    : `Income FMV (${taxPeriod}, filing)`
                }
                value={`€${taxableIncomeForReportingPeriod.totalEur.toFixed(2)}`}
              />
              <Stat
                label={
                  taxPeriod === 'all' ? 'Income rows (all time)' : `Income rows (${taxPeriod})`
                }
                value={String(taxableIncomeForReportingPeriod.rowCount)}
              />
            </HStack>
          </VStack>
          {disposalDetailsForView.length > 0 && (
            <Box
              mb={4}
              p={3}
              borderRadius="md"
              borderWidth="1px"
              borderColor={problemIndices.length ? 'orange.400' : 'whiteAlpha.300'}
              bg={problemIndices.length ? 'whiteAlpha.50' : 'whiteAlpha.100'}
            >
              <HStack align="flex-start" flexWrap="wrap" spacing={4} justify="space-between">
                <Box flex="1" minW="200px">
                  <Text fontSize="sm" fontWeight="semibold" mb={1}>
                    Disposal rows needing review
                  </Text>
                  <Text fontSize="xs" opacity={0.92}>
                    <strong>{problemIndices.length}</strong> of {disposalDetailsForView.length} row(s) match at
                    least one rule (valuation flags, deemed basis blocked, provenance, proceeds derivation, or zero
                    EUR cost).
                  </Text>
                  {problemIndices.length > 0 && (
                    <Text fontSize="xs" mt={2} opacity={0.88} lineHeight="short">
                      {formatDisposalAttentionBreakdown(byReason)}
                    </Text>
                  )}
                </Box>
                <HStack spacing={2} flexShrink={0} align="center">
                  <Button
                    size="sm"
                    variant="outline"
                    colorScheme="orange"
                    isDisabled={problemIndices.length === 0}
                    onClick={() => goProblemStep(-1)}
                  >
                    Previous
                  </Button>
                  <Text fontSize="xs" minW="72px" textAlign="center" fontFamily="mono">
                    {problemIndices.length ? `${problemNavFocus + 1} / ${problemIndices.length}` : '—'}
                  </Text>
                  <Button
                    size="sm"
                    variant="outline"
                    colorScheme="orange"
                    isDisabled={problemIndices.length === 0}
                    onClick={() => goProblemStep(1)}
                  >
                    Next
                  </Button>
                </HStack>
              </HStack>
            </Box>
          )}
          <Text fontSize="sm" mt={2} opacity={0.88} maxW="4xl">
            <strong>Cashflow (EUR)</strong> — same calendar period as the summary stats;{' '}
            <strong>separate from FIFO</strong> / capital gains. CDC fiat in = bank→app on-ramps (
            <code>fiat_to_crypto</code>, <code>crypto_viban_purchase</code>, <code>viban_purchase</code>). CDC to
            bank = fiat off-ramps (<code>crypto_to_fiat</code>, VIBAN exchange to EUR). Cronos income = on-chain
            taxable income FMV only (excludes Crypto.com CSV income).{' '}
            <strong>CDC fiat in:</strong> €{cashflowTotals.cdcFiatInEur.toFixed(2)} ·{' '}
            <strong>CDC to bank / fiat wallet:</strong> €{cashflowTotals.cdcBankOutEur.toFixed(2)} ·{' '}
            <strong>Cronos income (FMV):</strong> €{cashflowTotals.cronosIncomeEur.toFixed(2)}
          </Text>
          <Text fontSize="sm" mt={2} opacity={0.88} maxW="4xl">
            <strong>Audit (all time, EUR)</strong> — fiat from CDC cash CSV + crypto from ledger (not double-counted):{' '}
            <strong>Fiat deposits:</strong> €{auditReconciliationTotals.fiatDepositsEur.toFixed(2)} ·{' '}
            <strong>Fiat withdrawals:</strong> €{auditReconciliationTotals.fiatWithdrawalsEur.toFixed(2)} ·{' '}
            <strong>Card top-ups:</strong> €{auditReconciliationTotals.fiatCardTopUpEur.toFixed(2)} ·{' '}
            <strong>Crypto income FMV:</strong> €{auditReconciliationTotals.cryptoIncomeFmvEur.toFixed(2)} ·{' '}
            <strong>Crypto disposal proceeds:</strong> €{auditReconciliationTotals.cryptoDisposalProceedsEur.toFixed(2)}{' '}
            · <strong>Crypto acquisition basis:</strong> €{auditReconciliationTotals.cryptoAcquisitionBasisEur.toFixed(2)}
          </Text>

          {fifoDisplay.warnings.length > 0 && (
            <Alert status="info" borderRadius="md" mb={4} color="black">
              <AlertIcon />
              <Box fontSize="sm">
                {fifoDisplay.warnings.slice(0, 8).map((w, i) => (
                  <Text key={i}>{w}</Text>
                ))}
              </Box>
            </Alert>
          )}

          {disposalDetailsForView.length > 0 ? (
            <HStack align="center" flexWrap="wrap" gap={3} mt={4} mb={2}>
              <Button
                size="sm"
                variant="outline"
                colorScheme="green"
                onClick={() => setShowDisposalDetailTable((v) => !v)}
              >
                {showDisposalDetailTable ? 'Hide disposal detail table' : 'Show disposal detail table'}
              </Button>
              <Text fontSize="sm" opacity={0.88}>
                <strong>{disposalDetailsForView.length}</strong> row(s) — wide sortable table is{' '}
                <strong>hidden by default</strong> (use exports above without opening it).
              </Text>
            </HStack>
          ) : null}

          {showDisposalDetailTable && disposalDetailsForView.length > 0 ? (
            <Box
              overflowX="auto"
              maxH="min(70vh, 720px)"
              overflowY="auto"
              borderWidth="1px"
              borderColor="whiteAlpha.300"
              borderRadius="md"
            >
              <Table
                size="sm"
                variant="simple"
                sx={{
                  'thead th': {
                    position: 'sticky',
                    top: 0,
                    zIndex: 1,
                    bg: 'gray.800',
                    boxShadow: '0 1px 0 rgba(255,255,255,0.12)',
                  },
                  th: { whiteSpace: 'nowrap' },
                  td: { verticalAlign: 'top' },
                }}
              >
                <Thead>
                  <Tr>
                    <Th color="gray.200" w="44px">
                      #
                    </Th>
                    <SortableTh
                      columnId="csvTimestamp"
                    sortKey={disposalSort.key}
                    sortDir={disposalSort.dir}
                    onSort={toggleDisposalSort}
                  >
                    Timestamp (UTC)
                  </SortableTh>
                  <SortableTh
                    columnId="csvDesc"
                    sortKey={disposalSort.key}
                    sortDir={disposalSort.dir}
                    onSort={toggleDisposalSort}
                  >
                    Transaction description
                  </SortableTh>
                  <SortableTh
                    columnId="csvCurrency"
                    sortKey={disposalSort.key}
                    sortDir={disposalSort.dir}
                    onSort={toggleDisposalSort}
                  >
                    Currency
                  </SortableTh>
                  <SortableTh
                    columnId="csvAmount"
                    sortKey={disposalSort.key}
                    sortDir={disposalSort.dir}
                    onSort={toggleDisposalSort}
                  >
                    Amount
                  </SortableTh>
                  <SortableTh
                    columnId="csvToCurrency"
                    sortKey={disposalSort.key}
                    sortDir={disposalSort.dir}
                    onSort={toggleDisposalSort}
                  >
                    To currency
                  </SortableTh>
                  <SortableTh
                    columnId="csvToAmount"
                    sortKey={disposalSort.key}
                    sortDir={disposalSort.dir}
                    onSort={toggleDisposalSort}
                  >
                    To amount
                  </SortableTh>
                  <SortableTh
                    columnId="csvNativeCurrency"
                    sortKey={disposalSort.key}
                    sortDir={disposalSort.dir}
                    onSort={toggleDisposalSort}
                  >
                    Native currency
                  </SortableTh>
                  <SortableTh
                    columnId="csvNativeAmount"
                    sortKey={disposalSort.key}
                    sortDir={disposalSort.dir}
                    onSort={toggleDisposalSort}
                  >
                    Native amount
                  </SortableTh>
                  <SortableTh
                    columnId="csvNativeUsd"
                    sortKey={disposalSort.key}
                    sortDir={disposalSort.dir}
                    onSort={toggleDisposalSort}
                  >
                    Native amt (USD)
                  </SortableTh>
                  <SortableTh
                    columnId="csvKind"
                    sortKey={disposalSort.key}
                    sortDir={disposalSort.dir}
                    onSort={toggleDisposalSort}
                  >
                    Transaction kind
                  </SortableTh>
                  <SortableTh
                    columnId="csvHash"
                    sortKey={disposalSort.key}
                    sortDir={disposalSort.dir}
                    onSort={toggleDisposalSort}
                  >
                    Tx hash
                  </SortableTh>
                  <SortableTh
                    columnId="ts"
                    sortKey={disposalSort.key}
                    sortDir={disposalSort.dir}
                    onSort={toggleDisposalSort}
                  >
                    FIFO disposal (UTC)
                  </SortableTh>
                  <SortableTh
                    columnId="asset"
                    sortKey={disposalSort.key}
                    sortDir={disposalSort.dir}
                    onSort={toggleDisposalSort}
                  >
                    Asset
                  </SortableTh>
                  <SortableTh
                    columnId="disposed"
                    sortKey={disposalSort.key}
                    sortDir={disposalSort.dir}
                    onSort={toggleDisposalSort}
                    isNumeric
                  >
                    Qty
                  </SortableTh>
                  <SortableTh
                    columnId="proceedsEur"
                    sortKey={disposalSort.key}
                    sortDir={disposalSort.dir}
                    onSort={toggleDisposalSort}
                    isNumeric
                  >
                    Proceeds €
                  </SortableTh>
                  <SortableTh
                    columnId="costEur"
                    sortKey={disposalSort.key}
                    sortDir={disposalSort.dir}
                    onSort={toggleDisposalSort}
                    isNumeric
                  >
                    Cost € (FIFO)
                  </SortableTh>
                  <SortableTh
                    columnId="chosenCostBasisEur"
                    sortKey={disposalSort.key}
                    sortDir={disposalSort.dir}
                    onSort={toggleDisposalSort}
                    isNumeric
                  >
                    Cost € (filing)
                  </SortableTh>
                  <SortableTh
                    columnId="gainEur"
                    sortKey={disposalSort.key}
                    sortDir={disposalSort.dir}
                    onSort={toggleDisposalSort}
                    isNumeric
                  >
                    Gain / loss € (FIFO)
                  </SortableTh>
                  <SortableTh
                    columnId="chosenGainEur"
                    sortKey={disposalSort.key}
                    sortDir={disposalSort.dir}
                    onSort={toggleDisposalSort}
                    isNumeric
                  >
                    Gain / loss € (filing)
                  </SortableTh>
                  <SortableTh
                    columnId="basisMethodUsed"
                    sortKey={disposalSort.key}
                    sortDir={disposalSort.dir}
                  >
                    Basis used
                  </SortableTh>
                  <Th color="gray.200" isNumeric>
                    Result
                  </Th>
                  <SortableTh
                    columnId="source"
                    sortKey={disposalSort.key}
                    sortDir={disposalSort.dir}
                    onSort={toggleDisposalSort}
                  >
                    Source
                  </SortableTh>
                  <SortableTh
                    columnId="ref"
                    sortKey={disposalSort.key}
                    sortDir={disposalSort.dir}
                    onSort={toggleDisposalSort}
                  >
                    Reference
                  </SortableTh>
                  <Th color="gray.200" maxW="140px">
                    Origin wallet
                  </Th>
                  <Th color="gray.200" maxW="140px">
                    Current wallet
                  </Th>
                  <SortableTh
                    columnId="lotAcquiredAt"
                    sortKey={disposalSort.key}
                    sortDir={disposalSort.dir}
                    onSort={toggleDisposalSort}
                  >
                    Lot acquired (UTC)
                  </SortableTh>
                </Tr>
              </Thead>
              <Tbody>
                {disposalDetailsForView.map((d, i) => {
                  const iso = new Date(d.ts).toISOString();
                  const lotIso =
                    d.lotAcquiredAt != null ? new Date(d.lotAcquiredAt).toISOString() : null;
                  const m = d.csvMeta;
                  const chosenGain = d.chosenGainEur ?? d.gainEur;
                  const filingCost = d.chosenCostBasisEur ?? d.fifoCostBasisEur ?? d.costEur;
                  const basisUsed = d.basisMethodUsed ?? '—';
                  const attention = getDisposalAttentionMeta(d);
                  const isAttention = attention.needsAttention;
                  const isFocused =
                    problemIndices.length > 0 && problemIndices[problemNavFocus] === i;
                  return (
                    <Tr
                      key={`${d.ts}-${d.asset}-${d.ref ?? i}-${i}`}
                      id={`disposal-summary-row-${i}`}
                      transition="background 0.15s ease, box-shadow 0.15s ease"
                      bg={
                        isFocused
                          ? 'rgba(237, 137, 54, 0.22)'
                          : isAttention
                            ? 'whiteAlpha.50'
                            : undefined
                      }
                      borderLeftWidth={isAttention ? '3px' : undefined}
                      borderLeftColor={isAttention ? 'orange.400' : undefined}
                      boxShadow={
                        isFocused ? 'inset 0 0 0 1px rgba(237, 137, 54, 0.65)' : undefined
                      }
                    >
                      <Td color="gray.400" whiteSpace="nowrap">
                        {i + 1}
                        {d.fifoMergedFromSlices != null && d.fifoMergedFromSlices > 1 ? (
                          <Text as="span" display="block" fontSize="10px" opacity={0.75}>
                            ({d.fifoMergedFromSlices} FIFO lots)
                          </Text>
                        ) : null}
                      </Td>
                      <Td fontSize="xs" fontFamily="mono" maxW="120px" whiteSpace="normal" wordBreak="break-word">
                        {csvCell(m?.timestampUtc)}
                      </Td>
                      <Td fontSize="xs" maxW="200px" sx={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
                        {m?.transactionDescription != null && String(m.transactionDescription).trim() !== ''
                          ? renderTextWithCronosLinks(m.transactionDescription)
                          : '—'}
                      </Td>
                      <Td fontSize="xs" whiteSpace="nowrap">
                        {csvCell(m?.currency)}
                      </Td>
                      <Td fontSize="xs" fontFamily="mono" whiteSpace="nowrap">
                        {csvCell(m?.amount)}
                      </Td>
                      <Td fontSize="xs" whiteSpace="nowrap">
                        {csvCell(m?.toCurrency)}
                      </Td>
                      <Td fontSize="xs" fontFamily="mono" whiteSpace="nowrap">
                        {csvCell(m?.toAmount)}
                      </Td>
                      <Td fontSize="xs" whiteSpace="nowrap">
                        {csvCell(m?.nativeCurrency)}
                      </Td>
                      <Td fontSize="xs" fontFamily="mono" whiteSpace="nowrap">
                        {csvCell(m?.nativeAmount)}
                      </Td>
                      <Td fontSize="xs" fontFamily="mono" whiteSpace="nowrap">
                        {csvCell(m?.nativeAmountUsd)}
                      </Td>
                      <Td fontSize="xs" maxW="140px" sx={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
                        {csvCell(m?.transactionKind)}
                      </Td>
                      <Td fontSize="xs" fontFamily="mono" maxW="120px" sx={{ whiteSpace: 'normal', wordBreak: 'break-all' }}>
                        {m?.transactionHash != null && String(m.transactionHash).trim() !== '' ? (
                          <ExplorerTxOrAddressLink value={m.transactionHash} fontSize="xs" fontFamily="mono" />
                        ) : (
                          '—'
                        )}
                      </Td>
                      <Td maxW="200px">
                        <Text fontFamily="mono" fontSize="xs">
                          {iso.slice(0, 10)}
                        </Text>
                        <Text fontFamily="mono" fontSize="xs" opacity={0.85}>
                          {iso.slice(11, 19)}
                        </Text>
                        <Text
                          fontFamily="mono"
                          fontSize="10px"
                          opacity={0.65}
                          noOfLines={1}
                          title={iso}
                        >
                          {iso}
                        </Text>
                      </Td>
                      <Td fontWeight="medium" whiteSpace="nowrap">
                        {d.asset}
                      </Td>
                      <Td isNumeric fontFamily="mono" fontSize="xs" whiteSpace="nowrap">
                        {d.disposed.toFixed(8)}
                      </Td>
                      <Td isNumeric fontFamily="mono" fontSize="xs" whiteSpace="nowrap">
                        {d.proceedsEur.toFixed(2)}
                      </Td>
                      <Td isNumeric fontFamily="mono" fontSize="xs" whiteSpace="nowrap">
                        {d.costEur.toFixed(2)}
                      </Td>
                      <Td isNumeric fontFamily="mono" fontSize="xs" whiteSpace="nowrap">
                        {filingCost.toFixed(2)}
                      </Td>
                      <Td
                        isNumeric
                        fontFamily="mono"
                        fontSize="xs"
                        whiteSpace="nowrap"
                        color={d.gainEur.gte(0) ? 'green.300' : 'red.300'}
                      >
                        {d.gainEur.toFixed(2)}
                      </Td>
                      <Td
                        isNumeric
                        fontFamily="mono"
                        fontSize="xs"
                        whiteSpace="nowrap"
                        color={chosenGain.gte(0) ? 'green.300' : 'red.300'}
                      >
                        {chosenGain.toFixed(2)}
                      </Td>
                      <Td fontSize="xs" whiteSpace="nowrap">
                        {basisUsed}
                      </Td>
                      <Td isNumeric fontSize="xs" whiteSpace="nowrap">
                        {chosenGain.gte(0) ? 'Gain' : 'Loss'}
                      </Td>
                      <Td fontSize="xs" whiteSpace="nowrap">
                        {d.source}
                      </Td>
                      <Td
                        fontSize="xs"
                        fontFamily="mono"
                        maxW="220px"
                        sx={{ whiteSpace: 'normal', wordBreak: 'break-word' }}
                      >
                        <SummaryRefCell value={d.ref} />
                      </Td>
                      <Td fontSize="xs" maxW="140px" sx={{ whiteSpace: 'normal', wordBreak: 'break-all' }}>
                        {d.originWallet != null && String(d.originWallet).trim() !== '' ? (
                          <ExplorerTxOrAddressLink value={d.originWallet} fontSize="xs" fontFamily="mono" />
                        ) : (
                          <Text as="span" opacity={0.45} fontSize="xs">
                            —
                          </Text>
                        )}
                      </Td>
                      <Td fontSize="xs" maxW="140px" sx={{ whiteSpace: 'normal', wordBreak: 'break-all' }}>
                        {d.currentWallet != null && String(d.currentWallet).trim() !== '' ? (
                          <ExplorerTxOrAddressLink value={d.currentWallet} fontSize="xs" fontFamily="mono" />
                        ) : (
                          <Text as="span" opacity={0.45} fontSize="xs">
                            —
                          </Text>
                        )}
                      </Td>
                      <Td fontSize="xs" fontFamily="mono" maxW="200px">
                        {lotIso ? (
                          <>
                            <Text>{lotIso.slice(0, 10)}</Text>
                            <Text opacity={0.85}>{lotIso.slice(11, 19)}</Text>
                            <Text
                              fontSize="10px"
                              opacity={0.65}
                              noOfLines={1}
                              title={lotIso}
                            >
                              {lotIso}
                            </Text>
                          </>
                        ) : (
                          <Text opacity={0.5}>—</Text>
                        )}
                      </Td>
                    </Tr>
                  );
                })}
              </Tbody>
            </Table>
          </Box>
          ) : null}
          {fifoDisplay.details.length === 0 && (
            <Text fontSize="sm" opacity={0.8} mt={3}>
              {taxPeriod === 'all'
                ? 'No taxable disposals after FIFO. Import transactions that include sales or other disposals.'
                : `No taxable disposals in ${taxPeriod} after FIFO. Import earlier years so lots exist, try All time, or check that your CSV includes sells.`}
            </Text>
          )}
        </Box>
      )}

      {!fifoAll && ledgerEvents.length === 0 && (
        <Text textAlign="center" opacity={0.7}>
          Import a Crypto.com CSV and/or a Cronos address to begin.
        </Text>
      )}
    </VStack>
  );
}

function SortableTh({ children, columnId, sortKey, sortDir, onSort, isNumeric, ...rest }) {
  const active = sortKey === columnId;
  return (
    <Th
      {...rest}
      isNumeric={isNumeric}
      cursor="pointer"
      userSelect="none"
      onClick={() => onSort(columnId)}
      color="gray.200"
      _hover={{ bg: 'whiteAlpha.100' }}
      aria-sort={active ? (sortDir === 'asc' ? 'ascending' : 'descending') : 'none'}
    >
      <HStack spacing={1} justify={isNumeric ? 'flex-end' : 'flex-start'} w="100%">
        <Text as="span">{children}</Text>
        {active && (
          <Text as="span" fontSize="xs" aria-hidden>
            {sortDir === 'asc' ? '▲' : '▼'}
          </Text>
        )}
      </HStack>
    </Th>
  );
}

function Stat({ label, value }) {
  return (
    <VStack align="start" spacing={0}>
      <Text fontSize="xs" opacity={0.8}>
        {label}
      </Text>
      <Text fontWeight="bold" fontSize="xl">
        {value}
      </Text>
    </VStack>
  );
}

function TextareaMini({ value, onChange, rows = 4, placeholder = 'Or paste CSV text here…', ...rest }) {
  return (
    <Textarea
      rows={rows}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      bg="whiteAlpha.900"
      color="black"
      fontSize="sm"
      {...rest}
    />
  );
}

function mergeEvents(existing, incoming) {
  const map = new Map();
  const keyOf = (e) =>
    `${e.ts}|${e.source}|${e.kind}|${e.asset}|${e.ref || ''}|${e.amount.toString()}`;

  for (const e of existing) {
    map.set(keyOf(e), e);
  }
  for (const e of incoming) {
    map.set(keyOf(e), e);
  }
  return dedupeCryptoComVibanDisposals(Array.from(map.values())).sort((a, b) => a.ts - b.ts);
}

function mergeFiatCashEvents(existing, incoming) {
  const map = new Map();
  const keyOf = (f) => `${f.ts}|${f.reference}|${f.fiatSubkind}|${f.sourceType}`;
  for (const f of existing) {
    map.set(keyOf(f), f);
  }
  for (const f of incoming) {
    map.set(keyOf(f), f);
  }
  return Array.from(map.values()).sort((a, b) => a.ts - b.ts);
}
